import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "./api/client";
import AddToTradeIconButton from "./components/AddToTradeIconButton";
import { useAppDialog } from "./components/AppDialogProvider";
import BrowseUserIdentityLink from "./components/BrowseUserIdentityLink";
import CardAttributePill, { ConditionPill, LanguagePill } from "./components/CardAttributePill";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import UserConversationPanel from "./components/UserConversationPanel";
import UserIdentityLabel from "./components/UserIdentityLabel";
import { useCurrentUser } from "./currentUserContext";
import TraderModal from "./features/traders/components/TraderModal";
import { formatCustomPriceTrendComparison, getCustomPriceTrendComparison } from "./utils/customPriceTrendComparison";
import normalizeCondition from "./utils/normalizeCondition";
import { buildBrowseUserPath } from "./utils/browseUserPath";
import { formatEuroAmount, formatPaymentWithConversion, normalizePaymentCurrencyCode } from "./utils/pricingCurrency";
import usePaginationScroll from "./utils/usePaginationScroll";

const USER_ID = 1;
const TRADE_WORKSPACE_PAGE_SIZE = 15;
const LANGUAGE_LABELS = {
  en: "English",
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  jp: "Japanese",
  cn: "Chinese",
  kr: "Korean",
  ru: "Russian",
  pt: "Portuguese"
};

const EMPTY_ARRAY = [];
const DEFAULT_TRADE_FINDER_SETTINGS = {
  minSaleValue: 0,
  minBuyValue: 0,
  radiusKm: 0,
  socialEnabled: false,
  socialFriendUserIds: [],
  socialSelectedUserIds: [],
  locationLabel: null
};
const TAB_BROWSE = "browse";
const TAB_DRAFTS = "drafts";
const TAB_PENDING = "pending";
const TAB_ACCEPTED = "accepted";
const TAB_COMPLETED = "completed";
const TRADE_REVIEW_OPTIONS = [
  {
    value: 3,
    label: "Glad",
    tooltip: "Good",
    emoji: "😀",
    fill: "#22c55e",
    tint: "#dcfce7",
    borderColor: "#86efac"
  },
  {
    value: 2,
    label: "Midt i mellem",
    tooltip: "Neutral",
    emoji: "😐",
    fill: "#eab308",
    tint: "#fef9c3",
    borderColor: "#fde047"
  },
  {
    value: 1,
    label: "Sur",
    tooltip: "Bad",
    emoji: "🙁",
    fill: "#ef4444",
    tint: "#fee2e2",
    borderColor: "#fca5a5"
  }
];

const TRADE_ROW_QTY_STYLE = {
  color: "#475569",
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: 1.2
};

const TRADE_ROW_CARD_NAME_STYLE = {
  color: "#0f172a",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: 1.25,
  minWidth: 0,
  overflowWrap: "anywhere",
  wordBreak: "break-word"
};

const TRADE_PARTY_PILL_TEXT_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  maxWidth: "100%",
  padding: 0
};

const TRADE_PARTY_LINK_STYLE = {
  ...TRADE_PARTY_PILL_TEXT_STYLE,
  width: "100%",
  overflow: "hidden",
  whiteSpace: "nowrap"
};

const VISUALLY_HIDDEN_STYLE = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
};

function useMediaQuery(query) {
  const getMatches = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event) => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);

      return () => mediaQueryList.removeEventListener("change", handleChange);
    }

    mediaQueryList.addListener(handleChange);

    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
}

function formatCurrency(value) {
  return formatEuroAmount(value);
}

function formatTradeFinderSetting(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "Off";
  }

  return formatCurrency(numericValue);
}

function formatDistance(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  if (numericValue >= 100) {
    return `${Math.round(numericValue)} km away`;
  }

  return `${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: numericValue >= 10 ? 0 : 1,
    maximumFractionDigits: numericValue >= 10 ? 0 : 1
  })} km away`;
}

function createCurrentUserFallback(requestedUserId, selectedUser, tradeFinderSettings = DEFAULT_TRADE_FINDER_SETTINGS) {
  return {
    id: requestedUserId,
    username: selectedUser?.username || `User ${requestedUserId}`,
    tradeFinderSettings
  };
}

function normalizeTradeFinderCurrentUser(currentUser, requestedUserId, selectedUser, tradeFinderSettings = DEFAULT_TRADE_FINDER_SETTINGS) {
  const fallbackUser = createCurrentUserFallback(requestedUserId, selectedUser, tradeFinderSettings);

  if (!currentUser || Number(currentUser.id) !== Number(requestedUserId)) {
    return currentUser || fallbackUser;
  }

  return {
    ...currentUser,
    username: selectedUser?.username || currentUser.username || fallbackUser.username,
    tradeFinderSettings
  };
}

function formatCompletedDate(value) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(date);
}

function formatTradeFinderLocationSummary(locationLabel) {
  const locationParts = String(locationLabel || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (locationParts.length === 0) {
    return "Off";
  }

  if (locationParts.length === 1) {
    return locationParts[0];
  }

  return `${locationParts[0]}, ${locationParts[locationParts.length - 1]}`;
}

function formatTradeFinderRadiusLabel(settings = {}) {
  const locationLabel = String(settings.locationLabel || "").trim();
  const radiusKm = Number(settings.radiusKm);
  const locationSummary = formatTradeFinderLocationSummary(locationLabel);

  if (locationSummary === "Off") {
    return "Off";
  }

  if (!Number.isFinite(radiusKm) || radiusKm <= 0) {
    return `${locationSummary} · radius off`;
  }

  return `${locationSummary} · ${radiusKm} km`;
}

function formatTradeFinderSocialLabel(settings = {}) {
  return settings?.socialEnabled ? "On" : "Off";
}

function toNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function roundCurrency(value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

function normalizeLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function getLanguageLabel(languageCode) {
  if (!languageCode) {
    return "Unknown";
  }

  return LANGUAGE_LABELS[String(languageCode).toLowerCase()] || String(languageCode).toUpperCase();
}

function getEditionCode(edition) {
  return edition ? String(edition).toUpperCase() : "-";
}

function TradeMetaLabel({ children }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", minHeight: "20px" }}>
      {children}
    </span>
  );
}

function TradeEditionMeta({ edition, setMetadata }) {
  const normalizedEdition = normalizeLookupValue(edition);
  const metadata = normalizedEdition ? setMetadata?.[normalizedEdition] : null;
  const editionCode = metadata?.code || getEditionCode(edition);
  const tooltipLabel = metadata?.name || editionCode;

  return (
    <TradeMetaLabel>
      <span title={tooltipLabel} style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: "#334155" }}>
        {metadata?.iconSvgMarkup ? (
          <span
            aria-hidden="true"
            style={{
              width: "15px",
              height: "15px",
              color: "#475569",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            dangerouslySetInnerHTML={{ __html: metadata.iconSvgMarkup }}
          />
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "28px",
              height: "20px",
              padding: "0 6px",
              borderRadius: "999px",
              background: "#f8fafc",
              border: "1px solid #dbe4f0",
              color: "#475569",
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase"
            }}
          >
            {editionCode}
          </span>
        )}
      </span>
    </TradeMetaLabel>
  );
}

function TradeLanguageMeta({ language }) {
  if (!language) {
    return null;
  }

  return (
    <TradeMetaLabel>
      <LanguagePill language={language} title={getLanguageLabel(String(language).toLowerCase())} />
    </TradeMetaLabel>
  );
}

function TradeConditionMeta({ condition }) {
  if (!normalizeCondition(condition)) {
    return null;
  }

  return (
    <TradeMetaLabel>
      <ConditionPill condition={condition} />
    </TradeMetaLabel>
  );
}

function TradeFoilMeta({ foil }) {
  if (!foil) {
    return null;
  }

  return (
    <TradeMetaLabel>
      <CardAttributePill type="foil" title="Foil" />
    </TradeMetaLabel>
  );
}

function TradeAlteredMeta({ altered }) {
  if (!altered) {
    return null;
  }

  return (
    <TradeMetaLabel>
      <CardAttributePill type="altered" title="Altered" />
    </TradeMetaLabel>
  );
}

function TradeSignedMeta({ signed }) {
  if (!signed) {
    return null;
  }

  return (
    <TradeMetaLabel>
      <CardAttributePill type="signed" title="Signed" />
    </TradeMetaLabel>
  );
}

function TradeCardMeta({ card, setMetadata }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", rowGap: "4px", marginTop: "4px", alignItems: "center" }}>
      <TradeEditionMeta edition={card.edition} setMetadata={setMetadata} />
      <TradeLanguageMeta language={card.language} />
      <TradeConditionMeta condition={card.condition} />
      <TradeFoilMeta foil={card.foil} />
      <TradeAlteredMeta altered={card.altered} />
      <TradeSignedMeta signed={card.signed} />
    </div>
  );
}

function getExcludedCardToken(side, card) {
  return `${side}:${card.id}`;
}

function hasTradeCardVariantChoices(card) {
  return Boolean(card?.variant_group_key) && buildExpandedVariantChoices(card).length > 1;
}

function getTradeCardVariantSelectionKey(card = {}) {
  if (card.variant_selection_key) {
    return String(card.variant_selection_key);
  }

  if (card.id && String(card.id).includes(":copy:")) {
    return String(card.id);
  }

  return `listing:${card.source_listing_id || card.id || "card"}`;
}

function getTradeCardVariantGroupKey(card = {}) {
  return getTradeCardVariantSelectionKey(card).replace(/:copy:\d+$/, "");
}

function buildExpandedVariantChoices(baseCard) {
  if (!baseCard?.variant_group_key || !Array.isArray(baseCard.available_variants)) {
    return [];
  }

  return baseCard.available_variants.flatMap((variantCard) => {
    const copyCount = Math.max(1, Number(variantCard?.qty) || 1);
    const baseSelectionKey = getTradeCardVariantSelectionKey(variantCard);

    if (copyCount <= 1) {
      return [{
        ...variantCard,
        qty: 1,
        line_total: roundCurrency(roundCurrency(variantCard.user_price))
      }];
    }

    return Array.from({ length: copyCount }, (_, index) => ({
      ...variantCard,
      qty: 1,
      line_total: roundCurrency(roundCurrency(variantCard.user_price)),
      variant_selection_key: `${baseSelectionKey}:copy:${index + 1}`
    }));
  });
}

function buildGroupedVariantChoices(baseCard) {
  const expandedVariantChoices = buildExpandedVariantChoices(baseCard);

  if (expandedVariantChoices.length === 0) {
    return [];
  }

  const variantChoicesByGroupKey = new Map();

  expandedVariantChoices.forEach((variantCard) => {
    const groupKey = getTradeCardVariantGroupKey(variantCard);
    const selectionKey = getTradeCardVariantSelectionKey(variantCard);
    const existingChoice = variantChoicesByGroupKey.get(groupKey);

    if (existingChoice) {
      existingChoice.availableQty += 1;
      existingChoice.selectionKeys.push(selectionKey);
      return;
    }

    variantChoicesByGroupKey.set(groupKey, {
      ...variantCard,
      qty: 1,
      line_total: roundCurrency(roundCurrency(variantCard.user_price)),
      variant_group_choice_key: groupKey,
      availableQty: 1,
      selectionKeys: [selectionKey]
    });
  });

  return [...variantChoicesByGroupKey.values()];
}

function formatTradeCardVariantAvailabilitySummary(card) {
  const variantChoices = buildGroupedVariantChoices(card);

  if (variantChoices.length === 0) {
    return null;
  }

  const totalAvailable = variantChoices.reduce((sum, variantChoice) => sum + variantChoice.availableQty, 0);
  const versionLabel = variantChoices.length === 1 ? "version" : "versions";

  return `${variantChoices.length} ${versionLabel}, ${totalAvailable} total available`;
}

function collectTradeCardSetMetadataCards(cards = []) {
  return (Array.isArray(cards) ? cards : []).flatMap((card) => {
    if (!card) {
      return [];
    }

    const expandedVariantChoices = hasTradeCardVariantChoices(card)
      ? buildExpandedVariantChoices(card)
      : [];

    return [card, ...expandedVariantChoices];
  });
}

function describeTradeCardVariant(card = {}, copyIndex = null) {
  const details = [card.edition ? String(card.edition).toUpperCase() : null, card.condition || null, card.language ? String(card.language).toUpperCase() : null]
    .filter(Boolean)
    .join(" · ");
  const copyLabel = copyIndex ? `Copy ${copyIndex}` : null;

  return [details, copyLabel].filter(Boolean).join(" · ") || "Version";
}

function buildVariantSelectionQtyByGroup(baseCard, selectedKeys = []) {
  const selectedKeySet = new Set((Array.isArray(selectedKeys) ? selectedKeys : []).map((key) => String(key)));

  return buildGroupedVariantChoices(baseCard).reduce((counts, variantChoice) => {
    const selectedQty = variantChoice.selectionKeys.reduce((sum, selectionKey) => sum + (selectedKeySet.has(String(selectionKey)) ? 1 : 0), 0);

    if (selectedQty > 0) {
      counts.set(variantChoice.variant_group_choice_key, selectedQty);
    }

    return counts;
  }, new Map());
}

function resolvePreviewVariantGroupKey(baseCard, selectedKeys = [], lockedKeys = [], preferredGroupKey = null) {
  const variantChoices = buildGroupedVariantChoices(baseCard);

  if (variantChoices.length === 0) {
    return null;
  }

  if (preferredGroupKey) {
    const preferredVariantChoice = variantChoices.find((variantChoice) => variantChoice.variant_group_choice_key === preferredGroupKey);

    if (preferredVariantChoice) {
      return preferredVariantChoice.variant_group_choice_key;
    }
  }

  const selectedQtyByGroup = buildVariantSelectionQtyByGroup(baseCard, [...selectedKeys, ...lockedKeys]);
  const activeVariantChoice = variantChoices.find((variantChoice) => (selectedQtyByGroup.get(variantChoice.variant_group_choice_key) || 0) > 0);

  return activeVariantChoice?.variant_group_choice_key || variantChoices[0].variant_group_choice_key;
}

function replaceVariantSelectionKeysForChoice(baseCard, currentSelectedKeys = [], variantChoice = null, nextQty = 0) {
  if (!baseCard || !variantChoice) {
    return currentSelectedKeys;
  }

  const boundedQty = Math.max(0, Math.min(variantChoice.selectionKeys.length, Number(nextQty) || 0));
  const choiceSelectionKeySet = new Set(variantChoice.selectionKeys.map((selectionKey) => String(selectionKey)));
  const preservedSelectionKeys = new Set(
    (Array.isArray(currentSelectedKeys) ? currentSelectedKeys : [])
      .map((selectionKey) => String(selectionKey))
      .filter((selectionKey) => !choiceSelectionKeySet.has(selectionKey))
  );

  variantChoice.selectionKeys.slice(0, boundedQty).forEach((selectionKey) => {
    preservedSelectionKeys.add(String(selectionKey));
  });

  return buildExpandedVariantChoices(baseCard)
    .map((variantCard) => getTradeCardVariantSelectionKey(variantCard))
    .filter((selectionKey) => preservedSelectionKeys.has(String(selectionKey)));
}

function buildPreviewVariantSelectionState(baseCard, selectedGroupCards = [], targetCard = null) {
  if (!baseCard?.variant_group_key || !targetCard) {
    return { selectedKeys: [], lockedKeys: [], preferredGroupKey: null };
  }

  const selectedQtyByGroup = (Array.isArray(selectedGroupCards) ? selectedGroupCards : []).reduce((counts, draftCard) => {
    const groupKey = getTradeCardVariantGroupKey(draftCard);

    if (!groupKey) {
      return counts;
    }

    return {
      ...counts,
      [groupKey]: (counts[groupKey] || 0) + Math.max(1, Number(draftCard?.qty) || 1)
    };
  }, {});
  const targetGroupKey = getTradeCardVariantGroupKey(targetCard);
  let remainingTargetQty = Math.max(1, Number(targetCard?.qty) || 1);
  const selectedKeys = [];
  const lockedKeys = [];

  buildExpandedVariantChoices(baseCard).forEach((variantCard) => {
    const variantGroupKey = getTradeCardVariantGroupKey(variantCard);

    if (!selectedQtyByGroup[variantGroupKey]) {
      return;
    }

    const selectionKey = getTradeCardVariantSelectionKey(variantCard);

    if (variantGroupKey === targetGroupKey && remainingTargetQty > 0) {
      selectedKeys.push(selectionKey);
      selectedQtyByGroup[variantGroupKey] -= 1;
      remainingTargetQty -= 1;
      return;
    }

    lockedKeys.push(selectionKey);
    selectedQtyByGroup[variantGroupKey] -= 1;
  });

  return { selectedKeys, lockedKeys, preferredGroupKey: targetGroupKey || null };
}

function buildVariantSelectionCards(baseCard, selectedKeys = []) {
  const expandedVariantChoices = buildExpandedVariantChoices(baseCard);

  if (expandedVariantChoices.length === 0) {
    return [];
  }

  const selectedKeySet = new Set(selectedKeys.map((key) => String(key)));
  const groupedCards = new Map();

  expandedVariantChoices.forEach((variantCard) => {
    const selectionKey = getTradeCardVariantSelectionKey(variantCard);

    if (!selectedKeySet.has(selectionKey)) {
      return;
    }

    const groupingKey = Number(variantCard.source_listing_id) || selectionKey;
    const existingCard = groupedCards.get(groupingKey);

    if (existingCard) {
      const nextQty = existingCard.qty + 1;
      groupedCards.set(groupingKey, {
        ...existingCard,
        qty: nextQty,
        line_total: roundCurrency(nextQty * roundCurrency(existingCard.user_price))
      });
      return;
    }

    groupedCards.set(groupingKey, {
      ...variantCard,
      id: `${baseCard.variant_group_key}:${groupingKey}`,
      variant_group_key: baseCard.variant_group_key,
      available_variant_count: baseCard.available_variant_count,
      available_variants: Array.isArray(baseCard.available_variants) ? baseCard.available_variants : [],
      qty: 1,
      line_total: roundCurrency(roundCurrency(variantCard.user_price))
    });
  });

  return [...groupedCards.values()].sort((leftCard, rightCard) => {
    const lineTotalDifference = roundCurrency(rightCard.line_total) - roundCurrency(leftCard.line_total);

    if (lineTotalDifference !== 0) {
      return lineTotalDifference;
    }

    return String(leftCard.card || "").localeCompare(String(rightCard.card || ""));
  });
}

function withTradeUsers(trade, currentUser, counterparty) {
  return {
    ...(trade || {}),
    currentUser: trade?.currentUser || currentUser || null,
    counterparty: trade?.counterparty || counterparty || null
  };
}

function sumTradeCardValue(cards) {
  return roundCurrency(cards.reduce((sum, card) => sum + toNumber(card.line_total), 0));
}

function buildDisplayedTradeDraftCards(cards = [], side, excludedTokens = []) {
  const excludedTokenSet = new Set(excludedTokens);

  return (Array.isArray(cards) ? cards : []).flatMap((card) => {
    const isExcluded = excludedTokenSet.has(getExcludedCardToken(side, card));

    if (isExcluded && !card?.isSuggestedMatch) {
      return [];
    }

    return [{
      ...card,
      isExcluded
    }];
  });
}

function buildTradeDraft(trade, excludedTokens = []) {
  const displaySendCards = buildDisplayedTradeDraftCards(trade?.sendCards || [], "send", excludedTokens);
  const displayReceiveCards = buildDisplayedTradeDraftCards(trade?.receiveCards || [], "receive", excludedTokens);
  const sendCards = displaySendCards.filter((card) => !card.isExcluded);
  const receiveCards = displayReceiveCards.filter((card) => !card.isExcluded);

  const sendValue = sumTradeCardValue(sendCards);
  const receiveValue = sumTradeCardValue(receiveCards);
  let balanceDirection = "balanced";
  let paymentAmount = 0;
  let paymentFromUserId = null;

  if (receiveValue > sendValue) {
    balanceDirection = "current_user_pays";
    paymentAmount = roundCurrency(receiveValue - sendValue);
    paymentFromUserId = trade?.currentUser?.id || null;
  } else if (sendValue > receiveValue) {
    balanceDirection = "counterparty_pays";
    paymentAmount = roundCurrency(sendValue - receiveValue);
    paymentFromUserId = trade?.counterparty?.id || null;
  }

  return {
    ...trade,
    sendCards,
    receiveCards,
    displaySendCards,
    displayReceiveCards,
    sendValue,
    receiveValue,
    balanceDirection,
    paymentAmount,
    paymentFromUserId
  };
}

function serializeTradeCardForRequest(card = {}) {
  return {
    id: card.id || null,
    source_listing_id: card.source_listing_id ? Number(card.source_listing_id) : null,
    printing_id: card.printing_id ? Number(card.printing_id) : null,
    card: card.card || "Unknown card",
    edition: card.edition || null,
    language: card.language || null,
    condition: card.condition || null,
    foil: Boolean(card.foil),
    altered: Boolean(card.altered),
    signed: Boolean(card.signed),
    rarity: card.rarity || null,
    collector_number: card.collector_number || null,
    scryfall_id: card.scryfall_id || card.scryfallId || null,
    image: card.image || null,
    qty: Math.max(1, Number(card.qty) || 1),
    pricing_mode: card.pricing_mode || null,
    price_trend: card.price_trend ?? null,
    price_foil: card.price_foil ?? null,
    user_price: roundCurrency(card.user_price),
    line_total: roundCurrency(card.line_total || (Number(card.qty) || 1) * roundCurrency(card.user_price)),
    variant_group_key: card.variant_group_key || null,
    available_variant_count: card.available_variant_count ? Math.max(1, Number(card.available_variant_count) || 1) : null,
    available_variants: Array.isArray(card.available_variants)
      ? card.available_variants.map((variantCard) => ({
        id: variantCard.id || null,
        source_listing_id: variantCard.source_listing_id ? Number(variantCard.source_listing_id) : null,
        printing_id: variantCard.printing_id ? Number(variantCard.printing_id) : null,
        card: variantCard.card || "Unknown card",
        edition: variantCard.edition || null,
        language: variantCard.language || null,
        condition: variantCard.condition || null,
        foil: Boolean(variantCard.foil),
        altered: Boolean(variantCard.altered),
        signed: Boolean(variantCard.signed),
        rarity: variantCard.rarity || null,
        collector_number: variantCard.collector_number || null,
        scryfall_id: variantCard.scryfall_id || variantCard.scryfallId || null,
        image: variantCard.image || null,
        qty: Math.max(1, Number(variantCard.qty) || 1),
        pricing_mode: variantCard.pricing_mode || null,
        price_trend: variantCard.price_trend ?? null,
        price_foil: variantCard.price_foil ?? null,
        user_price: roundCurrency(variantCard.user_price),
        line_total: roundCurrency(variantCard.line_total || (Number(variantCard.qty) || 1) * roundCurrency(variantCard.user_price)),
        variant_group_key: variantCard.variant_group_key || card.variant_group_key || null,
        variant_selection_key: variantCard.variant_selection_key || null
      }))
      : null,
    variant_selection_key: card.variant_selection_key || null
  };
}

function getTradeCardMergeToken(card = {}) {
  if (card.source_listing_id) {
    return `listing:${card.source_listing_id}`;
  }

  return [
    card.printing_id || "",
    String(card.language || "").trim().toLowerCase(),
    String(card.condition || "").trim().toUpperCase(),
    card.foil ? "foil" : "nonfoil",
    card.altered ? "altered" : "clean",
    card.signed ? "signed" : "unsigned",
    String(card.card || "").trim().toLowerCase()
  ].join(":");
}

function mergeTradeCardCollections(primaryCards = [], additionalCards = []) {
  const cardsByToken = new Map();

  primaryCards.forEach((card) => {
    cardsByToken.set(getTradeCardMergeToken(card), card);
  });

  additionalCards.forEach((card) => {
    const token = getTradeCardMergeToken(card);

    if (!cardsByToken.has(token)) {
      cardsByToken.set(token, card);
    }
  });

  return [...cardsByToken.values()].sort((leftCard, rightCard) => {
    const lineTotalDifference = roundCurrency(rightCard?.line_total) - roundCurrency(leftCard?.line_total);

    if (lineTotalDifference !== 0) {
      return lineTotalDifference;
    }

    return String(leftCard?.card || "").localeCompare(String(rightCard?.card || ""));
  });
}

function getTradeWorkspaceStackToken(card = {}) {
  return [
    String(card.card || "").trim().toLowerCase(),
    String(card.edition || "").trim().toLowerCase(),
    String(card.language || "").trim().toLowerCase(),
    String(card.condition || "").trim().toUpperCase(),
    card.foil ? "foil" : "nonfoil",
    card.altered ? "altered" : "clean",
    card.signed ? "signed" : "unsigned",
    roundCurrency(card.user_price),
    String(card.variant_group_key || "")
  ].join(":");
}

function buildStackedTradeWorkspaceCards(cards = []) {
  const stackedCardsByToken = new Map();

  (Array.isArray(cards) ? cards : []).forEach((card) => {
    const token = getTradeWorkspaceStackToken(card);
    const existingCard = stackedCardsByToken.get(token);

    if (!existingCard) {
      stackedCardsByToken.set(token, {
        ...card,
        qty: Math.max(1, Number(card.qty) || 1),
        line_total: roundCurrency(card.line_total || Math.max(1, Number(card.qty) || 1) * roundCurrency(card.user_price))
      });
      return;
    }

    const nextQty = Math.max(1, Number(existingCard.qty) || 1) + Math.max(1, Number(card.qty) || 1);
    const nextLineTotal = roundCurrency(
      roundCurrency(existingCard.line_total || 0)
      + roundCurrency(card.line_total || Math.max(1, Number(card.qty) || 1) * roundCurrency(card.user_price))
    );

    stackedCardsByToken.set(token, {
      ...existingCard,
      qty: nextQty,
      line_total: nextLineTotal
    });
  });

  return [...stackedCardsByToken.values()].sort((leftCard, rightCard) => {
    const lineTotalDifference = roundCurrency(rightCard?.line_total) - roundCurrency(leftCard?.line_total);

    if (lineTotalDifference !== 0) {
      return lineTotalDifference;
    }

    return String(leftCard?.card || "").localeCompare(String(rightCard?.card || ""));
  });
}

function filterTradeCardsNotAlreadyInDraft(cards = [], draftCards = []) {
  const draftTokens = new Set((Array.isArray(draftCards) ? draftCards : []).map((card) => getTradeCardMergeToken(card)));

  return (Array.isArray(cards) ? cards : []).filter((card) => !draftTokens.has(getTradeCardMergeToken(card)));
}

function buildBrowseSuggestionDraftDelta(suggestion, draft) {
  if (!suggestion) {
    return null;
  }

  if (!draft) {
    return {
      ...suggestion,
      receiveCards: markBrowseSuggestedCards(suggestion.receiveCards || []),
      sendCards: markBrowseSuggestedCards(suggestion.sendCards || [])
    };
  }

  const nextReceiveCards = filterTradeCardsNotAlreadyInDraft(suggestion.receiveCards || [], draft.demandCards || []);
  const nextSendCards = filterTradeCardsNotAlreadyInDraft(suggestion.sendCards || [], draft.offerCards || []);

  if (nextReceiveCards.length === 0 && nextSendCards.length === 0) {
    return null;
  }

  return {
    ...suggestion,
    receiveCards: markBrowseSuggestedCards(nextReceiveCards),
    sendCards: markBrowseSuggestedCards(nextSendCards),
    matchedWantCount: nextReceiveCards.reduce((sum, card) => sum + (Number(card.qty) || 0), 0),
    matchedWantStacks: nextReceiveCards.length,
    matchedOfferCount: nextSendCards.reduce((sum, card) => sum + (Number(card.qty) || 0), 0),
    matchedOfferStacks: nextSendCards.length,
    receiveValue: sumTradeCardValue(nextReceiveCards),
    sendValue: sumTradeCardValue(nextSendCards),
    draftExpiresAt: draft.expiresAt,
    hasDraftAdditions: true
  };
}

function markBrowseSuggestedCards(cards = []) {
  return (Array.isArray(cards) ? cards : []).map((card) => ({
    ...card,
    isSuggestedMatch: true,
    isExcluded: false
  }));
}

function markBrowseDraftCards(cards = []) {
  return (Array.isArray(cards) ? cards : []).map((card) => ({
    ...card,
    isSuggestedMatch: false,
    isExcluded: false
  }));
}

function createBrowseSuggestionFromDraft(draft, currentUser) {
  return {
    counterparty: draft.counterparty,
    distanceKm: null,
    receiveCards: markBrowseDraftCards(draft.demandCards || []),
    sendCards: markBrowseDraftCards(draft.offerCards || []),
    matchedWantCount: Array.isArray(draft.demandCards) ? draft.demandCards.reduce((sum, card) => sum + (Number(card.qty) || 0), 0) : 0,
    matchedWantStacks: Array.isArray(draft.demandCards) ? draft.demandCards.length : 0,
    matchedOfferCount: Array.isArray(draft.offerCards) ? draft.offerCards.reduce((sum, card) => sum + (Number(card.qty) || 0), 0) : 0,
    matchedOfferStacks: Array.isArray(draft.offerCards) ? draft.offerCards.length : 0,
    receiveValue: sumTradeCardValue(Array.isArray(draft.demandCards) ? draft.demandCards : []),
    sendValue: sumTradeCardValue(Array.isArray(draft.offerCards) ? draft.offerCards : []),
    balanceAmount: 0,
    balanceDirection: "balanced",
    currentUser,
    draftExpiresAt: draft.expiresAt,
    hasDraftAdditions: true,
    isDraftOnly: true
  };
}

function formatDraftExpiryLabel(expiresAt) {
  const expiresAtTimestamp = new Date(expiresAt).getTime();

  if (!Number.isFinite(expiresAtTimestamp)) {
    return null;
  }

  const remainingMs = expiresAtTimestamp - Date.now();

  if (remainingMs <= 0) {
    return "Draft expires soon";
  }

  const remainingMinutes = Math.ceil(remainingMs / 60000);

  if (remainingMinutes >= 60) {
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;
    return minutes > 0 ? `Draft expires in ${hours}h ${minutes}m` : `Draft expires in ${hours}h`;
  }

  return `Draft expires in ${remainingMinutes}m`;
}

function TradeCardRow({ card, onClick, onToggleExclude, setMetadata }) {
  const hasVariantChoices = hasTradeCardVariantChoices(card) && !card.is_variant_addition;
  const isExcluded = Boolean(card?.isExcluded);
  const canExclude = typeof onToggleExclude === "function";
  const customPriceComparison = getCustomPriceTrendComparison(card, card.user_price, card.pricing_mode);
  const customPriceComparisonColor = customPriceComparison?.tone === "warning" ? "#ea580c" : "#15803d";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{
        display: "grid",
        gridTemplateColumns: "56px 1fr auto",
        gap: "12px",
        alignItems: "center",
        padding: "12px 0",
        border: "none",
        borderBottom: "1px solid #e2e8f0",
        background: "transparent",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        opacity: isExcluded ? 0.46 : 1
      }}
    >
      <div
        style={{
          width: "56px",
          height: "78px",
          borderRadius: "6px",
          overflow: "hidden",
          background: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
        }}
      >
        {card.image ? (
          <img src={card.image} alt={card.card} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 700 }}>No image</span>
        )}
      </div>
      <div>
        <span style={VISUALLY_HIDDEN_STYLE}>{`${card.qty || 1}x ${card.card}`}</span>
        <div style={{ minWidth: 0 }}>
          <span style={TRADE_ROW_CARD_NAME_STYLE}>
            <span style={TRADE_ROW_QTY_STYLE}>{card.qty || 1}x</span>{" "}
            {card.card}
          </span>
        </div>
        <TradeCardMeta card={card} setMetadata={setMetadata} />
        {hasVariantChoices ? (
          <div style={{ marginTop: "6px", fontSize: "11px", color: "#0f766e", fontWeight: 700 }}>
            {formatTradeCardVariantAvailabilitySummary(card)}
          </div>
        ) : null}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: canExclude ? "auto auto" : "auto", columnGap: "8px", alignItems: "start", justifyContent: "end", textAlign: "right" }}>
        <div>
          <div style={{ fontSize: "12px", color: "#0f172a", fontWeight: 700 }}>{formatCurrency(card.line_total)}</div>
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>{formatCurrency(card.user_price)} each</div>
          {customPriceComparison ? (
            <div style={{ fontSize: "11px", color: customPriceComparisonColor, marginTop: "4px", fontWeight: 700 }}>
              {formatCustomPriceTrendComparison(customPriceComparison.percentDelta, { compactPercent: true })}
            </div>
          ) : null}
        </div>
        {canExclude ? (
          isExcluded ? (
            <AddToTradeIconButton
              cardName={card.card}
              onClick={() => {
                onToggleExclude(card);
              }}
            />
          ) : (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleExclude(card);
              }}
              className="tradefinder-row-exclude-button"
              aria-label="Exclude from trade"
              title="Exclude from trade"
            >
              X
            </button>
          )
        ) : null}
      </div>
    </div>
  );
}

function CounterpartyNameButton({ username, collectorTier, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="user-identity-link user-identity-link--truncate"
      style={{
        minWidth: 0,
        maxWidth: "100%",
        color: "#0f766e",
        textAlign: "left",
        overflowWrap: "anywhere",
        wordBreak: "break-word"
      }}
    >
      <UserIdentityLabel username={username} tier={collectorTier} size="sm" color="#0f766e" fontWeight={700} wrap={false} truncateAt={18} />
    </button>
  );
}

function TradeSummaryBadges({ theyHaveCount, theyWantCount, distanceLabel }) {
  return (
    <div style={{ display: "flex", gap: "8px 10px", flexWrap: "wrap", marginTop: "10px", minWidth: 0 }}>
      <span style={{ fontSize: "12px", color: "#0f766e", fontWeight: 700 }}>They have: {theyHaveCount}</span>
      <span style={{ fontSize: "12px", color: "#1d4ed8", fontWeight: 700 }}>They want: {theyWantCount}</span>
      {distanceLabel ? <span style={{ fontSize: "12px", color: "#7c3aed", fontWeight: 700 }}>{distanceLabel}</span> : null}
    </div>
  );
}

function SuggestionListItem({ suggestion, isActive, onClick, onOpenCounterparty }) {
  const distanceLabel = formatDistance(suggestion.distanceKm);
  const draftExpiryLabel = suggestion.draftExpiresAt ? formatDraftExpiryLabel(suggestion.draftExpiresAt) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        textAlign: "left",
        padding: "16px",
        borderRadius: "16px",
        border: isActive ? "1px solid #0f766e" : "1px solid #dbe4f0",
        background: isActive ? "linear-gradient(180deg, #ecfeff 0%, #f8fafc 100%)" : "#ffffff",
        boxShadow: isActive ? "0 10px 24px rgba(15, 118, 110, 0.12)" : "0 8px 20px rgba(15, 23, 42, 0.04)",
        cursor: "pointer"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <CounterpartyNameButton
            username={suggestion.counterparty.username}
            collectorTier={suggestion.counterparty.collectorTier}
            onClick={(event) => {
              event.stopPropagation();
              onOpenCounterparty?.(suggestion.counterparty);
            }}
          />
        </div>
      </div>
      <TradeSummaryBadges
        theyHaveCount={suggestion.matchedWantCount}
        theyWantCount={suggestion.matchedOfferCount}
        distanceLabel={distanceLabel}
      />
      {draftExpiryLabel ? (
        <div style={{ marginTop: "10px", fontSize: "12px", fontWeight: 600, color: "#0f766e" }}>
          {draftExpiryLabel}
        </div>
      ) : null}
    </div>
  );
}

function MobileTradeListToggle({ totalCount, hiddenCount, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        textAlign: "left",
        padding: "16px",
        borderRadius: "16px",
        border: "1px solid #dbe4f0",
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
        cursor: "pointer"
      }}
      aria-label={`Show all trades ${totalCount}`}
    >
      <div
        style={{
          fontSize: "13px",
          fontWeight: 700,
          color: "#000000",
          textDecoration: "underline",
          textUnderlineOffset: "3px"
        }}
      >
        {`Show all trades (${totalCount})`}
      </div>
      <div style={{ marginTop: "8px", fontSize: "12px", color: "#475569", fontWeight: 600 }}>
        {hiddenCount === 1 ? "1 hidden trade" : `${hiddenCount} hidden trades`}
      </div>
    </div>
  );
}

function ResponsiveTradeSidebar({
  items,
  activeIndex,
  onSelectIndex,
  renderItem,
  getItemKey,
  emptyState,
  modalTitle = "All trades"
}) {
  const isMobileViewport = useMediaQuery("(max-width: 780px)");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsModalOpen(false);
    }
  }, [isMobileViewport]);

  if (items.length === 0) {
    return emptyState;
  }

  if (!isMobileViewport) {
    return items.map((item, index) => (
      <Fragment key={getItemKey(item, index)}>
        {renderItem(item, index, index === activeIndex, () => onSelectIndex(index))}
      </Fragment>
    ));
  }

  const normalizedActiveIndex = activeIndex >= 0 && activeIndex < items.length ? activeIndex : 0;
  const activeItem = items[normalizedActiveIndex];
  const hiddenItems = items.flatMap((item, index) => (index === normalizedActiveIndex ? [] : [{ item, index }]));
  const modalItems = items.map((item, index) => ({ item, index }));

  return (
    <>
      <Fragment key={getItemKey(activeItem, normalizedActiveIndex)}>
        {renderItem(activeItem, normalizedActiveIndex, true, () => onSelectIndex(normalizedActiveIndex))}
      </Fragment>
      {hiddenItems.length > 0 ? (
        <>
          <MobileTradeListToggle
            totalCount={items.length}
            hiddenCount={hiddenItems.length}
            onClick={() => setIsModalOpen(true)}
          />
          <TraderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={modalTitle} width="560px">
            <div style={{ display: "grid", gap: "12px" }}>
              {modalItems.map(({ item, index }) => (
                <Fragment key={getItemKey(item, index)}>
                  {renderItem(item, index, index === normalizedActiveIndex, () => {
                    onSelectIndex(index);
                    setIsModalOpen(false);
                  })}
                </Fragment>
              ))}
            </div>
          </TraderModal>
        </>
      ) : null}
    </>
  );
}

function TradeDealListItem({ deal, isActive, onClick, onOpenCounterparty }) {
  const distanceLabel = formatDistance(deal.distanceKm);
  const completedDateLabel = deal.status === "completed" ? formatCompletedDate(deal.completedAt) : null;
  const theyWantCount = (deal.sendCards || []).reduce((sum, card) => sum + Math.max(1, Number(card.qty) || 1), 0);
  const theyHaveCount = (deal.receiveCards || []).reduce((sum, card) => sum + Math.max(1, Number(card.qty) || 1), 0);
  const statusColor = deal.status === "completed"
    ? "#15803d"
    : deal.awaitingResponse
      ? "#b45309"
      : deal.status === "accepted"
        ? "#0f766e"
        : "#64748b";
  const statusLabel = deal.status === "accepted"
    ? "Accepted"
    : deal.status === "completed"
      ? "Completed"
    : deal.awaitingResponse
      ? "Needs response"
      : deal.awaitingCounterparty
        ? "Awaiting response"
        : "Pending";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      style={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        textAlign: "left",
        padding: "16px",
        borderRadius: "16px",
        border: isActive ? "1px solid #0f766e" : "1px solid #dbe4f0",
        background: isActive ? "linear-gradient(180deg, #ecfeff 0%, #f8fafc 100%)" : "#ffffff",
        boxShadow: isActive ? "0 10px 24px rgba(15, 118, 110, 0.12)" : "0 8px 20px rgba(15, 23, 42, 0.04)",
        cursor: "pointer"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <CounterpartyNameButton
            username={deal.counterparty.username}
            collectorTier={deal.counterparty.collectorTier}
            onClick={(event) => {
              event.stopPropagation();
              onOpenCounterparty?.(deal.counterparty);
            }}
          />
        </div>
        <div style={{ fontSize: "12px", color: statusColor, fontWeight: 700 }}>{statusLabel}</div>
      </div>
      <TradeSummaryBadges
        theyHaveCount={theyHaveCount}
        theyWantCount={theyWantCount}
        distanceLabel={distanceLabel}
      />
      {completedDateLabel ? (
        <div style={{ marginTop: "6px", fontSize: "11px", color: "#64748b", fontWeight: 600 }}>
          Completed {completedDateLabel}
        </div>
      ) : null}
    </div>
  );
}

function TabButton({ label, isActive, badge, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="app-button-base tradefinder-tab-button"
      data-active={isActive ? "true" : "false"}
      style={{
        border: isActive ? "1px solid #0f766e" : "1px solid #cbd5e1",
        background: isActive ? "#ecfeff" : "#ffffff",
        color: isActive ? "#0f766e" : "#334155",
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer"
      }}
    >
      <span className="tradefinder-tab-button__label">{label}</span>
      {badge ? (
        <span className="tradefinder-tab-button__badge">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function TradeWorkspacePagination({ currentPage, totalPages, totalItems, onPageChange }) {
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  if (totalPages <= 1) {
    return null;
  }

  const commitPageInput = () => {
    const nextPage = Number.parseInt(pageInput, 10);

    if (!Number.isFinite(nextPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setPageInput(String(clampedPage));

    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div className="app-pagination" style={{ paddingTop: "12px" }}>
      <div className="app-pagination__summary" style={{ fontSize: "12px" }}>
        Page {currentPage} of {totalPages} · {totalItems} cards
      </div>
      <div className="app-pagination__controls">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          title="Previous page"
          disabled={currentPage <= 1}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage <= 1 ? "#f8fafc" : "#ffffff",
            color: currentPage <= 1 ? "#94a3b8" : "#0f172a",
            cursor: currentPage <= 1 ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{"<"}</span>
        </button>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          title="Next page"
          disabled={currentPage >= totalPages}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage >= totalPages ? "#f8fafc" : "#ffffff",
            color: currentPage >= totalPages ? "#94a3b8" : "#0f172a",
            cursor: currentPage >= totalPages ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{">"}</span>
        </button>
        <label className="app-pagination__jump">
          Page
          <div className="app-pagination__jump-controls">
            <div className="app-pagination__select-shell">
              <input
                type="number"
                min="1"
                max={String(totalPages)}
                step="1"
                inputMode="numeric"
                aria-label="Jump to page"
                className="app-pagination__page-input"
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitPageInput();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setPageInput(String(currentPage));
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="app-pagination__button app-pagination__button--text app-pagination__jump-button"
              onClick={commitPageInput}
            >
              Go
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}

function TradeCardPreviewModal({ preview, isSavingSelection, onClose, onToggleExclude, onToggleVariantSelection, onSaveVariantSelection, setMetadata }) {
  const card = preview?.card || null;
  const canViewVariants = Boolean(preview?.canViewVariants);
  const canManageVariants = Boolean(preview?.canManageVariants);
  const selectedVariantKeys = preview?.selectedVariantKeys || EMPTY_ARRAY;
  const lockedVariantKeys = preview?.lockedVariantKeys || EMPTY_ARRAY;
  const variantChoices = hasTradeCardVariantChoices(card) && canViewVariants && !card.is_variant_addition
    ? buildGroupedVariantChoices(card)
    : [];
  const selectedVariantQtyByGroup = buildVariantSelectionQtyByGroup(card, selectedVariantKeys);
  const lockedVariantQtyByGroup = buildVariantSelectionQtyByGroup(card, lockedVariantKeys);
  const [activePreviewVariantKey, setActivePreviewVariantKey] = useState(() => resolvePreviewVariantGroupKey(card, selectedVariantKeys, lockedVariantKeys, preview?.preferredGroupKey));
  const variantAvailabilitySummary = formatTradeCardVariantAvailabilitySummary(card);

  useEffect(() => {
    setActivePreviewVariantKey(resolvePreviewVariantGroupKey(card, selectedVariantKeys, lockedVariantKeys, preview?.preferredGroupKey));
  }, [card, preview?.contextKey, preview?.preferredGroupKey, preview?.side, selectedVariantKeys, lockedVariantKeys]);

  const activePreviewVariantIndex = variantChoices.findIndex((variantCard) => variantCard.variant_group_choice_key === activePreviewVariantKey);
  const activePreviewVariant = activePreviewVariantIndex >= 0 ? variantChoices[activePreviewVariantIndex] : null;
  const displayCard = activePreviewVariant || card;
  const previewPriceComparison = getCustomPriceTrendComparison(displayCard, displayCard?.user_price, displayCard?.pricing_mode);
  const previewPriceComparisonColor = previewPriceComparison?.tone === "warning" ? "#ea580c" : "#15803d";

  if (!card) {
    return null;
  }

  return (
    <TraderModal
      isOpen={Boolean(preview?.card)}
      onClose={onClose}
      title={displayCard.card || card.card || "Trade card"}
      width="900px"
    >
      <div className="tradefinder-preview-layout">
        <div className="tradefinder-preview-image-shell">
          {displayCard.imageNormal || displayCard.image ? (
            <img
              src={displayCard.imageNormal || displayCard.image}
              alt={displayCard.card}
              className="tradefinder-preview-image"
            />
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center", color: "#64748b", fontWeight: 700 }}>No image</div>
          )}
        </div>
        <div className="tradefinder-preview-copy">
          <div>
            <div style={{ fontSize: "28px", fontWeight: 800, color: "#0f172a" }}>{displayCard.card}</div>
            <TradeCardMeta card={displayCard} setMetadata={setMetadata} />
          </div>
          <div className="tradefinder-preview-meta">
            <div><strong>Quantity:</strong> {displayCard.qty}</div>
            <div className="tradefinder-preview-unit-value">
              <div>
                <strong>Unit value:</strong> {formatCurrency(displayCard.user_price)} each
                {previewPriceComparison ? (
                  <span style={{ marginLeft: "6px", fontSize: "12px", fontWeight: 700, color: previewPriceComparisonColor }}>
                    {formatCustomPriceTrendComparison(previewPriceComparison.percentDelta, { compactPercent: true })}
                  </span>
                ) : null}
              </div>
            </div>
            <div><strong>Line total:</strong> {formatCurrency(displayCard.line_total)}</div>
          </div>
          {variantChoices.length > 0 ? (
            <div style={{ display: "grid", gap: "12px" }}>
              <div style={{ fontSize: "14px", fontWeight: 800, color: "#0f172a" }}>
                Available printings ({variantChoices.length})
              </div>
              {variantAvailabilitySummary ? (
                <div style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                  {variantAvailabilitySummary}
                </div>
              ) : null}
              {!canManageVariants ? (
                <div style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                  Save this trade to Drafts before choosing which version to request.
                </div>
              ) : null}
              <div style={{ display: "grid", gap: "10px", maxHeight: "280px", overflowY: "auto", paddingRight: "4px" }}>
                {variantChoices.map((variantCard) => {
                  const selectedQty = selectedVariantQtyByGroup.get(variantCard.variant_group_choice_key) || 0;
                  const lockedQty = lockedVariantQtyByGroup.get(variantCard.variant_group_choice_key) || 0;
                  const maxSelectableQty = Math.max(0, variantCard.availableQty - lockedQty);
                  const isChecked = selectedQty > 0;
                  const isActivePreview = activePreviewVariantKey === variantCard.variant_group_choice_key;
                  const isLockedForOtherTradeRow = maxSelectableQty === 0 && selectedQty === 0;
                  const variantLabel = `${variantCard.card || "Card"} ${describeTradeCardVariant(variantCard)}`;
                  const variantPriceComparison = getCustomPriceTrendComparison(variantCard, variantCard.user_price, variantCard.pricing_mode);
                  const variantPriceComparisonColor = variantPriceComparison?.tone === "warning" ? "#ea580c" : "#15803d";
                  const statusText = lockedQty > 0
                    ? (maxSelectableQty === 0 ? `Already in trade (${lockedQty})` : `${lockedQty} already in trade`)
                    : `${variantCard.availableQty} available`;

                  return (
                    <div
                      key={`${card.variant_group_key || card.id}:${variantCard.variant_group_choice_key}`}
                      className={`tradefinder-variant-card${isChecked ? " tradefinder-variant-card--selected" : ""}${isActivePreview ? " tradefinder-variant-card--active" : ""}${isLockedForOtherTradeRow ? " tradefinder-variant-card--disabled" : ""}`}
                      onClick={() => setActivePreviewVariantKey(variantCard.variant_group_choice_key)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setActivePreviewVariantKey(variantCard.variant_group_choice_key);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Preview ${variantLabel}`}
                    >
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>{variantCard.card}</div>
                        <TradeCardMeta card={variantCard} setMetadata={setMetadata} />
                        <div style={{ marginTop: "4px", fontSize: "12px", color: "#475569", fontWeight: 700 }}>
                          {formatCurrency(variantCard.user_price)} each
                          {variantPriceComparison ? (
                            <span style={{ marginLeft: "6px", color: variantPriceComparisonColor, fontWeight: 700 }}>
                              {formatCustomPriceTrendComparison(variantPriceComparison.percentDelta, { compactPercent: true })}
                            </span>
                          ) : null}
                        </div>
                        <div className="tradefinder-variant-card__status-row">
                          <span className={`tradefinder-variant-card__status${lockedQty > 0 ? " tradefinder-variant-card__status--locked" : ""}`}>
                            {statusText}
                          </span>
                        </div>
                      </div>
                      {canManageVariants ? (
                        <div className="tradefinder-variant-card__actions">
                          <label
                            className={`tradefinder-variant-checkbox${isLockedForOtherTradeRow ? " tradefinder-variant-checkbox--disabled" : ""}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="account-auth-consent__checkbox tradefinder-variant-checkbox__input"
                              checked={isChecked || isLockedForOtherTradeRow}
                              disabled={isLockedForOtherTradeRow}
                              aria-label={isLockedForOtherTradeRow
                                ? `${variantLabel} already in trade`
                                : `${isChecked ? "Remove" : "Add"} ${variantLabel}`}
                              onChange={(event) => {
                                event.stopPropagation();
                                setActivePreviewVariantKey(variantCard.variant_group_choice_key);
                                onToggleVariantSelection?.(variantCard, event.target.checked ? 1 : 0);
                              }}
                            />
                          </label>
                          <div className="tradefinder-variant-qty-shell" onClick={(event) => event.stopPropagation()}>
                            <select
                              aria-label={`Quantity of ${variantLabel} in trade`}
                              disabled={!isChecked || maxSelectableQty < 1}
                              value={Math.max(1, selectedQty || 1)}
                              className="tradefinder-variant-qty-select"
                              onChange={(event) => {
                                event.stopPropagation();
                                setActivePreviewVariantKey(variantCard.variant_group_choice_key);
                                onToggleVariantSelection?.(variantCard, Math.max(1, Number(event.target.value) || 1));
                              }}
                            >
                              {Array.from({ length: Math.max(1, maxSelectableQty) }, (_, optionIndex) => optionIndex + 1).map((qtyOption) => (
                                <option key={`${variantCard.variant_group_choice_key}:${qtyOption}`} value={qtyOption}>
                                  {qtyOption}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {canManageVariants ? (
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontSize: "12px", color: "#475569", fontWeight: 700 }}>
                    {selectedVariantKeys.length} selected for this row
                  </div>
                  <button
                    type="button"
                    onClick={onSaveVariantSelection}
                    disabled={isSavingSelection}
                    className="app-button-base tradefinder-variant-save-button"
                    style={{
                      cursor: isSavingSelection ? "wait" : "pointer",
                      opacity: isSavingSelection ? 0.8 : 1
                    }}
                  >
                    {isSavingSelection ? "Saving..." : "Save selection"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </TraderModal>
  );
}

function TradeWorkspace({
  trade,
  currentUser,
  counterparty,
  contextKey,
  variantEdits,
  setMetadata,
  pricingCurrencySettings,
  pricingExchangeRates,
  excludedCards,
  onCardPreview,
  onToggleExcludedCard,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionDisabled,
  primaryActionTone = "green",
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionDisabled,
  secondaryActionTone = "neutral",
  secondaryMessage,
  onOpenCounterparty,
  sidePanel,
  title,
  subtitle
}) {
  const tradeWithUsers = withTradeUsers(trade, currentUser, counterparty);
  const tradeDraft = buildTradeDraft(tradeWithUsers, excludedCards, variantEdits);
  const currentUserId = Number(tradeWithUsers.currentUser?.id) || null;
  const counterpartyUserId = Number(tradeWithUsers.counterparty?.id) || null;
  const currentUserName = tradeWithUsers.currentUser?.username || "Current user";
  const currentUserCollectorTier = tradeWithUsers.currentUser?.collectorTier;
  const counterpartyName = tradeWithUsers.counterparty?.username || "Counterparty";
  const counterpartyCollectorTier = tradeWithUsers.counterparty?.collectorTier;
  const [sendPage, setSendPage] = useState(1);
  const [receivePage, setReceivePage] = useState(1);
  const sendResultsRef = usePaginationScroll(sendPage);
  const receiveResultsRef = usePaginationScroll(receivePage);
  const stackedSendCards = buildStackedTradeWorkspaceCards(tradeDraft.displaySendCards || tradeDraft.sendCards);
  const stackedReceiveCards = buildStackedTradeWorkspaceCards(tradeDraft.displayReceiveCards || tradeDraft.receiveCards);
  const sendCardCount = (tradeDraft.sendCards || []).reduce((sum, card) => sum + Math.max(1, Number(card.qty) || 1), 0);
  const receiveCardCount = (tradeDraft.receiveCards || []).reduce((sum, card) => sum + Math.max(1, Number(card.qty) || 1), 0);
  const sendPageCount = Math.max(1, Math.ceil(stackedSendCards.length / TRADE_WORKSPACE_PAGE_SIZE));
  const receivePageCount = Math.max(1, Math.ceil(stackedReceiveCards.length / TRADE_WORKSPACE_PAGE_SIZE));
  const visibleSendCards = stackedSendCards.slice((sendPage - 1) * TRADE_WORKSPACE_PAGE_SIZE, sendPage * TRADE_WORKSPACE_PAGE_SIZE);
  const visibleReceiveCards = stackedReceiveCards.slice((receivePage - 1) * TRADE_WORKSPACE_PAGE_SIZE, receivePage * TRADE_WORKSPACE_PAGE_SIZE);
  const tradePartyPillStyle = {
    ...TRADE_PARTY_PILL_TEXT_STYLE
  };

  useEffect(() => {
    setSendPage(1);
    setReceivePage(1);
  }, [contextKey]);

  useEffect(() => {
    if (sendPage > sendPageCount) {
      setSendPage(sendPageCount);
    }
  }, [sendPage, sendPageCount]);

  useEffect(() => {
    if (receivePage > receivePageCount) {
      setReceivePage(receivePageCount);
    }
  }, [receivePage, receivePageCount]);

  return (
    <div className="tradefinder-workspace">
      <div className="tradefinder-workspace-header-panel">
        <div className="tradefinder-workspace-header">
          <div style={{ minWidth: 0, flex: "1 1 auto" }}>
            <div style={{ fontSize: "12px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>{subtitle}</div>
            <div style={{ fontSize: "24px", fontWeight: 800, color: "#0f172a", marginTop: "6px", minWidth: 0, maxWidth: "100%", lineHeight: 1.15, overflowWrap: "anywhere", wordBreak: "break-word" }}>{title}</div>
          </div>
          <div className="tradefinder-workspace-actions">
            {secondaryMessage ? (
              <div className="tradefinder-workspace-status-pill">
                {secondaryMessage}
              </div>
            ) : null}
            {primaryActionLabel ? (
              <button
                type="button"
                disabled={primaryActionDisabled}
                onClick={onPrimaryAction}
                className="app-button-base tradefinder-workspace-action-button"
                data-tone={primaryActionTone}
              >
                {primaryActionLabel}
              </button>
            ) : null}
            {secondaryActionLabel ? (
              <button
                type="button"
                disabled={secondaryActionDisabled}
                onClick={onSecondaryAction}
                className="app-button-base tradefinder-workspace-action-button"
                data-tone={secondaryActionTone}
              >
                {secondaryActionLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className={`tradefinder-workspace-columns${sidePanel ? " tradefinder-workspace-columns--with-side-panel" : ""}`}>
        <div className="tradefinder-workspace-column">
          <div className="tradefinder-workspace-column-header">
            <div>
              <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>Offer</div>
              <div style={{ marginTop: "8px" }}>
                {currentUserId ? (
                  <BrowseUserIdentityLink
                    to={buildBrowseUserPath(currentUserId, currentUserName)}
                    state={{ userId: currentUserId, username: currentUserName, collectorTier: currentUserCollectorTier || null }}
                    username={currentUserName}
                    tier={currentUserCollectorTier}
                    color="#1e3a8a"
                    className="user-identity-link--truncate"
                    style={{ ...TRADE_PARTY_LINK_STYLE, color: "#1e3a8a" }}
                  />
                ) : (
                  <span style={{ ...tradePartyPillStyle, color: "#1e3a8a" }}>
                    <UserIdentityLabel username={currentUserName} tier={currentUserCollectorTier} size="sm" color="#1e3a8a" fontWeight={700} wrap={false} truncateAt={18} />
                  </span>
                )}
              </div>
            </div>
            <div style={{ fontSize: "13px", color: "#1d4ed8", fontWeight: 700 }}>{sendCardCount} {sendCardCount === 1 ? "card" : "cards"}</div>
          </div>
          <div ref={sendResultsRef} style={{ marginTop: "12px" }}>
            {stackedSendCards.length > 0 ? visibleSendCards.map((card) => (
              <TradeCardRow
                key={`send-${contextKey}-${card.id}`}
                card={card}
                setMetadata={setMetadata}
                onToggleExclude={onToggleExcludedCard ? (targetCard) => onToggleExcludedCard(contextKey, "send", targetCard) : null}
                onClick={() => onCardPreview({ contextKey, side: "send", card })}
              />
            )) : (
              <div style={{ color: "#64748b", padding: "12px 0" }}>No cards currently included from this side.</div>
            )}
            <TradeWorkspacePagination
              currentPage={sendPage}
              totalPages={sendPageCount}
              totalItems={stackedSendCards.length}
              onPageChange={setSendPage}
            />
          </div>
          <div className="tradefinder-workspace-column-footer">
            <div>
              <div style={{ fontSize: "12px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                Cards total value
              </div>
              <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginTop: "8px" }}>
                {formatCurrency(tradeDraft.sendValue)}
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginTop: "12px" }}>
                Payment
              </div>
              {tradeDraft.balanceDirection === "current_user_pays" ? (
                <>
                  <div className="tradefinder-workspace-payment" style={{ marginTop: "8px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 800 }}>{formatPaymentWithConversion(tradeDraft.paymentAmount, pricingCurrencySettings, pricingExchangeRates)}</span>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginTop: "8px" }}>
                  -
                </div>
              )}
            </div>
            <div className="tradefinder-workspace-balance">
              {tradeDraft.balanceDirection === "balanced" ? (
                <div className="tradefinder-workspace-balanced-note">
                  Balanced trade
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="tradefinder-workspace-column">
          <div className="tradefinder-workspace-column-header">
            <div>
              <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>Demand</div>
              <div style={{ marginTop: "8px" }}>
                {counterpartyUserId ? (
                  <BrowseUserIdentityLink
                    to={buildBrowseUserPath(counterpartyUserId, counterpartyName)}
                    state={{ userId: counterpartyUserId, username: counterpartyName, collectorTier: counterpartyCollectorTier || null }}
                    onClick={() => onOpenCounterparty?.(tradeWithUsers.counterparty)}
                    username={counterpartyName}
                    tier={counterpartyCollectorTier}
                    color="#155e75"
                    className="user-identity-link--truncate"
                    style={{ ...TRADE_PARTY_LINK_STYLE, color: "#155e75" }}
                  />
                ) : (
                  <span style={{ ...tradePartyPillStyle, color: "#155e75" }}>
                    <UserIdentityLabel username={counterpartyName} tier={counterpartyCollectorTier} size="sm" color="#155e75" fontWeight={700} wrap={false} truncateAt={18} />
                  </span>
                )}
              </div>
            </div>
            <div style={{ fontSize: "13px", color: "#0f766e", fontWeight: 700 }}>{receiveCardCount} {receiveCardCount === 1 ? "card" : "cards"}</div>
          </div>
          <div ref={receiveResultsRef} style={{ marginTop: "12px" }}>
            {stackedReceiveCards.length > 0 ? visibleReceiveCards.map((card) => (
              <TradeCardRow
                key={`receive-${contextKey}-${card.id}`}
                card={card}
                setMetadata={setMetadata}
                onToggleExclude={onToggleExcludedCard ? (targetCard) => onToggleExcludedCard(contextKey, "receive", targetCard) : null}
                onClick={() => onCardPreview({ contextKey, side: "receive", card })}
              />
            )) : (
              <div style={{ color: "#64748b", padding: "12px 0" }}>No cards currently included from this side.</div>
            )}
            <TradeWorkspacePagination
              currentPage={receivePage}
              totalPages={receivePageCount}
              totalItems={stackedReceiveCards.length}
              onPageChange={setReceivePage}
            />
          </div>
          <div className="tradefinder-workspace-column-footer">
            <div>
              <div style={{ fontSize: "12px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                Cards total value
              </div>
              <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginTop: "8px" }}>
                {formatCurrency(tradeDraft.receiveValue)}
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginTop: "12px" }}>
                Payment
              </div>
              {tradeDraft.balanceDirection === "counterparty_pays" ? (
                <>
                  <div className="tradefinder-workspace-payment" style={{ marginTop: "8px" }}>
                    <span style={{ fontSize: "16px", fontWeight: 800 }}>{formatPaymentWithConversion(tradeDraft.paymentAmount, pricingCurrencySettings, pricingExchangeRates)}</span>
                  </div>
                </>
              ) : (
                <div style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a", marginTop: "8px" }}>
                  -
                </div>
              )}
            </div>
            <div className="tradefinder-workspace-balance">
              {tradeDraft.balanceDirection === "balanced" ? (
                <div className="tradefinder-workspace-balanced-note">
                  Balanced trade
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {sidePanel ? sidePanel : null}
      </div>
    </div>
  );
}

function createTradeCompletionFormState() {
  return {
    removeMatchedWants: true,
    satisfactionRating: 3,
    reviewRemarks: ""
  };
}

function createTradeMessageFormState() {
  return {
    messageBody: ""
  };
}

function formatTradeAvailabilityConflicts(conflicts = []) {
  const relevantConflicts = (Array.isArray(conflicts) ? conflicts : []).filter(Boolean);

  if (relevantConflicts.length === 0) {
    return "This draft needs review before it can be proposed.";
  }

  const details = relevantConflicts
    .slice(0, 3)
    .map((conflict) => `${conflict.cardName || "Card"}: requested ${conflict.requestedQty}, available ${conflict.availableQty}`)
    .join(". ");

  return `This draft needs review before it can be proposed. ${details}.`;
}

function ProposeTradeModal({ isOpen, trade, value, isSubmitting, onClose, onChange, onSubmit }) {
  if (!isOpen || !trade) {
    return null;
  }

  return (
    <TraderModal isOpen={isOpen} onClose={onClose} title="Propose Trade" width="620px">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        style={{ display: "grid", gap: "18px" }}
      >
        <div style={{ padding: "14px 16px", borderRadius: "14px", background: "#f8fafc", border: "1px solid #dbe4f0", color: "#334155" }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "6px" }}>
            Trade Summary
          </div>
          <div style={{ lineHeight: 1.6 }}>
            You are proposing a trade to <strong>{trade.counterparty?.username || "the other user"}</strong>.
            You can send an optional first message right away to coordinate the next step.
          </div>
        </div>

        <label style={{ display: "grid", gap: "8px", fontSize: "13px", fontWeight: 700, color: "#334155" }}>
          <span>Message</span>
          <textarea
            value={value.messageBody}
            onChange={(event) => onChange("messageBody", event.target.value.slice(0, 1200))}
            rows={5}
            placeholder="Optional message about meetup details, shipping, timing, or anything else the other user should know"
            style={{
              width: "100%",
              boxSizing: "border-box",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              padding: "12px 14px",
              resize: "vertical",
              color: "#0f172a"
            }}
          />
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, justifySelf: "end" }}>
            {value.messageBody.length} / 1200
          </span>
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              minWidth: "120px",
              height: "42px",
              padding: "0 16px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#334155",
              fontWeight: 700,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              minWidth: "150px",
              height: "42px",
              padding: "0 16px",
              borderRadius: "12px",
              border: "1px solid #0f766e",
              background: "#0f766e",
              color: "#ffffff",
              fontWeight: 700,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
          >
            {isSubmitting ? "Sending..." : "Propose trade"}
          </button>
        </div>
      </form>
    </TraderModal>
  );
}

function CompleteTradeModal({ isOpen, trade, value, isSubmitting, onClose, onChange, onSubmit }) {
  if (!isOpen || !trade) {
    return null;
  }

  return (
    <TraderModal isOpen={isOpen} onClose={onClose} title="Complete Trade" width="620px">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        style={{ display: "grid", gap: "18px" }}
      >
        <div style={{ padding: "14px 16px", borderRadius: "14px", background: "#f8fafc", border: "1px solid #dbe4f0", color: "#334155" }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "6px" }}>
            Trade Summary
          </div>
          <div style={{ lineHeight: 1.6 }}>
            You are completing the trade with <strong>{trade.counterparty?.username || "the other user"}</strong>.
            Admins can see the review below. Other users cannot.
          </div>
        </div>

        <label style={{ display: "flex", gap: "12px", alignItems: "flex-start", padding: "14px 16px", borderRadius: "14px", border: "1px solid #dbe4f0", background: "#ffffff", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={value.removeMatchedWants}
            onChange={(event) => onChange("removeMatchedWants", event.target.checked)}
            style={{ marginTop: "3px" }}
          />
          <span style={{ fontWeight: 700, color: "#0f172a" }}>Remove matching cards from My Wants</span>
        </label>

        <div style={{ display: "grid", gap: "10px" }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#334155" }}>How was your experience with this user?</div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {TRADE_REVIEW_OPTIONS.map((option) => {
              const isActive = Number(value.satisfactionRating) === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  aria-label={option.label}
                  title={option.tooltip}
                  onClick={() => onChange("satisfactionRating", option.value)}
                  style={{
                    width: "78px",
                    height: "78px",
                    padding: "0",
                    borderRadius: "18px",
                    border: isActive ? `2px solid ${option.fill}` : `1px solid ${option.borderColor}`,
                    background: isActive ? option.tint : "#ffffff",
                    color: option.fill,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyItems: "center",
                    justifyContent: "center",
                    boxShadow: isActive ? "0 10px 24px rgba(15, 23, 42, 0.10)" : "none"
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      fontSize: "36px",
                      lineHeight: 1,
                      filter: isActive ? "none" : "saturate(0.92)"
                    }}
                  >
                    {option.emoji}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <label style={{ display: "grid", gap: "8px", fontSize: "13px", fontWeight: 700, color: "#334155" }}>
          <span>Remarks</span>
          <textarea
            value={value.reviewRemarks}
            onChange={(event) => onChange("reviewRemarks", event.target.value.slice(0, 600))}
            rows={4}
            placeholder="Optional notes for admins about the trade or the other user"
            style={{
              width: "100%",
              boxSizing: "border-box",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              padding: "12px 14px",
              resize: "vertical",
              color: "#0f172a"
            }}
          />
          <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, justifySelf: "end" }}>
            {value.reviewRemarks.length} / 600
          </span>
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              minWidth: "120px",
              height: "42px",
              padding: "0 16px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#334155",
              fontWeight: 700,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              minWidth: "150px",
              height: "42px",
              padding: "0 16px",
              borderRadius: "12px",
              border: "1px solid #0f766e",
              background: "#0f766e",
              color: "#ffffff",
              fontWeight: 700,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
          >
            {isSubmitting ? "Completing..." : "Complete trade"}
          </button>
        </div>
      </form>
    </TraderModal>
  );
}

export default function TradeFinderView() {
  const { showAlert, showConfirm } = useAppDialog();
  const navigate = useNavigate();
  const { selectedUserId, selectedUser } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedUserId = selectedUserId || USER_ID;
  const handledPendingTradeIdRef = useRef(null);
  const handledAcceptedTradeIdRef = useRef(null);
  const handledCounterpartyUserIdRef = useRef(null);
  const [browsePayload, setBrowsePayload] = useState({
    currentUser: createCurrentUserFallback(requestedUserId, selectedUser),
    suggestions: [],
    summary: { totalSuggestions: 0, totalSuggestionsBeforeRadius: 0, radiusFilteredSuggestions: 0, totalReceiveMatches: 0, totalSendMatches: 0 }
  });
  const [activeTradesPayload, setActiveTradesPayload] = useState({
    pending: [],
    accepted: [],
    summary: { pendingCount: 0, acceptedCount: 0, awaitingResponseCount: 0, awaitingCounterpartyCount: 0 }
  });
  const [completedTradesPayload, setCompletedTradesPayload] = useState({
    deals: [],
    summary: { completedCount: 0 }
  });
  const [browseDrafts, setBrowseDrafts] = useState([]);
  const [activeTab, setActiveTab] = useState(TAB_BROWSE);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [activePendingTradeId, setActivePendingTradeId] = useState(null);
  const [activeAcceptedTradeId, setActiveAcceptedTradeId] = useState(null);
  const [activeCompletedTradeId, setActiveCompletedTradeId] = useState(null);
  const [excludedCardsByContext, setExcludedCardsByContext] = useState({});
  const [cardPreview, setCardPreview] = useState(null);
  const [isProposeTradeModalOpen, setIsProposeTradeModalOpen] = useState(false);
  const [proposeTradeForm, setProposeTradeForm] = useState(() => createTradeMessageFormState());
  const [isCompleteTradeModalOpen, setIsCompleteTradeModalOpen] = useState(false);
  const [completeTradeForm, setCompleteTradeForm] = useState(() => createTradeCompletionFormState());
  const [isSavingVariantSelection, setIsSavingVariantSelection] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [draftAvailabilityConflictsByCounterpartyId, setDraftAvailabilityConflictsByCounterpartyId] = useState({});
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [hasLoadedBrowseSuggestions, setHasLoadedBrowseSuggestions] = useState(false);
  const [loadingTrades, setLoadingTrades] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [setMetadata, setSetMetadata] = useState({});
  const [pricingCurrencySettings, setPricingCurrencySettings] = useState({
    paymentConversionEnabled: false,
    paymentCurrencyCode: normalizePaymentCurrencyCode(null)
  });
  const [pricingExchangeRates, setPricingExchangeRates] = useState({});
  const requestedCounterpartyUserId = Number.parseInt(searchParams.get("counterpartyUserId") || "", 10);
  const requestedPendingTradeId = Number.parseInt(searchParams.get("pendingTradeId") || "", 10);
  const requestedAcceptedTradeId = Number.parseInt(searchParams.get("acceptedTradeId") || "", 10);

  const clearDraftAvailabilityConflict = useCallback((counterpartyId) => {
    const numericCounterpartyId = Number(counterpartyId);

    if (!Number.isInteger(numericCounterpartyId) || numericCounterpartyId < 1) {
      return;
    }

    setDraftAvailabilityConflictsByCounterpartyId((currentState) => {
      if (!Object.prototype.hasOwnProperty.call(currentState, numericCounterpartyId)) {
        return currentState;
      }

      const nextState = { ...currentState };
      delete nextState[numericCounterpartyId];
      return nextState;
    });
  }, []);

  useEffect(() => {
    let isDisposed = false;

    const loadPricingCurrencySettings = async () => {
      try {
        const response = await apiFetch(`/api/account/pricing-currency-settings/${requestedUserId}`, { userId: requestedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load Pricing and Currency settings");
        }

        if (isDisposed) {
          return;
        }

        setPricingCurrencySettings({
          paymentConversionEnabled: Boolean(data?.settings?.paymentConversionEnabled),
          paymentCurrencyCode: normalizePaymentCurrencyCode(data?.settings?.paymentCurrencyCode)
        });
        setPricingExchangeRates(data?.exchangeRates?.rates || {});
      } catch (settingsError) {
        if (!isDisposed) {
          console.error("TradeFinder pricing currency settings load error:", settingsError);
          setPricingCurrencySettings({
            paymentConversionEnabled: false,
            paymentCurrencyCode: normalizePaymentCurrencyCode(null)
          });
          setPricingExchangeRates({});
        }
      }
    };

    loadPricingCurrencySettings();

    return () => {
      isDisposed = true;
    };
  }, [requestedUserId]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    handledCounterpartyUserIdRef.current = null;
    setBrowsePayload({
      currentUser: createCurrentUserFallback(requestedUserId, selectedUser),
      suggestions: [],
      summary: { totalSuggestions: 0, totalSuggestionsBeforeRadius: 0, radiusFilteredSuggestions: 0, totalReceiveMatches: 0, totalSendMatches: 0 }
    });
    setActiveSuggestionIndex(0);
    setHasLoadedBrowseSuggestions(false);
    setLoadingBrowse(false);
  }, [requestedUserId, selectedUser]);

  useEffect(() => {
    let isDisposed = false;

    const loadTradeFinderSettingsSummary = async () => {
      try {
        const response = await apiFetch(`/api/account/tradefinder-settings/${requestedUserId}`, { userId: requestedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load TradeFinder settings");
        }

        if (isDisposed) {
          return;
        }

        setBrowsePayload((currentPayload) => ({
          ...currentPayload,
          currentUser: normalizeTradeFinderCurrentUser(
            currentPayload.currentUser,
            requestedUserId,
            selectedUser,
            data?.settings || DEFAULT_TRADE_FINDER_SETTINGS
          )
        }));
      } catch (settingsError) {
        if (!isDisposed) {
          console.error("TradeFinder settings summary load error:", settingsError);
        }
      }
    };

    loadTradeFinderSettingsSummary();

    return () => {
      isDisposed = true;
    };
  }, [requestedUserId, selectedUser]);

  useEffect(() => {
    let isDisposed = false;

    const loadTradeDrafts = async () => {
      try {
        const response = await apiFetch("/api/trade-drafts", { userId: requestedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load trade drafts");
        }

        if (!isDisposed) {
          setBrowseDrafts(Array.isArray(data.drafts) ? data.drafts : []);
        }
      } catch (err) {
        if (!isDisposed) {
          console.error("Trade draft load error:", err);
          setBrowseDrafts([]);
        }
      }
    };

    loadTradeDrafts();

    return () => {
      isDisposed = true;
    };
  }, [requestedUserId]);

  const browseSuggestions = (Array.isArray(browsePayload.suggestions) ? browsePayload.suggestions : []).flatMap((suggestion) => {
    const matchingDraft = browseDrafts.find((draft) => Number(draft?.counterparty?.id) === Number(suggestion?.counterparty?.id));
    const draftDeltaSuggestion = buildBrowseSuggestionDraftDelta(suggestion, matchingDraft);

    return draftDeltaSuggestion ? [draftDeltaSuggestion] : [];
  });
  const activeDraft = browseDrafts.find((draft) => Number(draft?.id) === Number(activeDraftId)) || browseDrafts[0] || null;
  const activeDraftSuggestion = activeDraft ? createBrowseSuggestionFromDraft(activeDraft, browsePayload.currentUser) : null;

  useEffect(() => {
    if (browseSuggestions.length === 0 && activeSuggestionIndex !== 0) {
      setActiveSuggestionIndex(0);
    }
  }, [activeSuggestionIndex, browseSuggestions.length]);

  useEffect(() => {
    if (browseDrafts.length === 0) {
      if (activeDraftId !== null) {
        setActiveDraftId(null);
      }
      return;
    }

    const hasActiveDraft = browseDrafts.some((draft) => Number(draft?.id) === Number(activeDraftId));

    if (!hasActiveDraft) {
      setActiveDraftId(browseDrafts[0].id);
    }
  }, [activeDraftId, browseDrafts]);

  useEffect(() => {
    if (!Number.isInteger(requestedCounterpartyUserId) || requestedCounterpartyUserId <= 0) {
      handledCounterpartyUserIdRef.current = null;
      return;
    }

    if (handledCounterpartyUserIdRef.current === requestedCounterpartyUserId) {
      return;
    }

    const matchingDraft = browseDrafts.find((draft) => Number(draft?.counterparty?.id) === requestedCounterpartyUserId);

    if (matchingDraft) {
      handledCounterpartyUserIdRef.current = requestedCounterpartyUserId;
      setActiveTab(TAB_DRAFTS);
      setActiveDraftId(matchingDraft.id);

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("counterpartyUserId");
      setSearchParams(nextParams, { replace: true });
      return;
    }

    if (!hasLoadedBrowseSuggestions) {
      return;
    }

    const matchingSuggestionIndex = browseSuggestions.findIndex(
      (suggestion) => Number(suggestion?.counterparty?.id) === requestedCounterpartyUserId
    );

    handledCounterpartyUserIdRef.current = requestedCounterpartyUserId;

    if (matchingSuggestionIndex >= 0) {
      setActiveTab(TAB_BROWSE);
      if (matchingSuggestionIndex !== activeSuggestionIndex) {
        setActiveSuggestionIndex(matchingSuggestionIndex);
      }
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("counterpartyUserId");
    setSearchParams(nextParams, { replace: true });
  }, [
    activeSuggestionIndex,
    activeDraftId,
    browseDrafts,
    browseSuggestions,
    hasLoadedBrowseSuggestions,
    requestedCounterpartyUserId,
    searchParams,
    setSearchParams
  ]);

  useEffect(() => {
    if (!Number.isInteger(requestedPendingTradeId) || requestedPendingTradeId < 1) {
      handledPendingTradeIdRef.current = null;
      return;
    }

    if (handledPendingTradeIdRef.current === requestedPendingTradeId) {
      return;
    }

    handledPendingTradeIdRef.current = requestedPendingTradeId;

    if (activeTab !== TAB_PENDING) {
      setActiveTab(TAB_PENDING);
    }

    if (activePendingTradeId !== requestedPendingTradeId) {
      setActivePendingTradeId(requestedPendingTradeId);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("pendingTradeId");
    setSearchParams(nextParams, { replace: true });
  }, [activePendingTradeId, activeTab, requestedPendingTradeId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!Number.isInteger(requestedAcceptedTradeId) || requestedAcceptedTradeId < 1) {
      handledAcceptedTradeIdRef.current = null;
      return;
    }

    if (handledAcceptedTradeIdRef.current === requestedAcceptedTradeId) {
      return;
    }

    handledAcceptedTradeIdRef.current = requestedAcceptedTradeId;

    if (activeTab !== TAB_ACCEPTED) {
      setActiveTab(TAB_ACCEPTED);
    }

    if (activeAcceptedTradeId !== requestedAcceptedTradeId) {
      setActiveAcceptedTradeId(requestedAcceptedTradeId);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("acceptedTradeId");
    setSearchParams(nextParams, { replace: true });
  }, [activeAcceptedTradeId, activeTab, requestedAcceptedTradeId, searchParams, setSearchParams]);

  useEffect(() => {
    const activePendingDeal = activeTradesPayload.pending.find((deal) => Number(deal.id) === Number(activePendingTradeId)) || activeTradesPayload.pending[0] || null;
    const activeAcceptedDeal = activeTradesPayload.accepted.find((deal) => Number(deal.id) === Number(activeAcceptedTradeId)) || activeTradesPayload.accepted[0] || null;
    const activeCompletedDeal = completedTradesPayload.deals.find((deal) => Number(deal.id) === Number(activeCompletedTradeId)) || completedTradesPayload.deals[0] || null;

    let visibleCards = [];

    if (activeTab === TAB_BROWSE) {
      const activeSuggestion = browseSuggestions[activeSuggestionIndex] || null;
      visibleCards = activeSuggestion
        ? collectTradeCardSetMetadataCards([...(activeSuggestion.sendCards || []), ...(activeSuggestion.receiveCards || [])])
        : [];
    } else if (activeTab === TAB_DRAFTS) {
      visibleCards = activeDraftSuggestion
        ? collectTradeCardSetMetadataCards([...(activeDraftSuggestion.sendCards || []), ...(activeDraftSuggestion.receiveCards || [])])
        : [];
    } else if (activeTab === TAB_PENDING) {
      visibleCards = activePendingDeal
        ? collectTradeCardSetMetadataCards([...(activePendingDeal.sendCards || []), ...(activePendingDeal.receiveCards || [])])
        : [];
    } else if (activeTab === TAB_ACCEPTED) {
      visibleCards = activeAcceptedDeal
        ? collectTradeCardSetMetadataCards([...(activeAcceptedDeal.sendCards || []), ...(activeAcceptedDeal.receiveCards || [])])
        : [];
    } else if (activeTab === TAB_COMPLETED) {
      visibleCards = activeCompletedDeal
        ? collectTradeCardSetMetadataCards([...(activeCompletedDeal.sendCards || []), ...(activeCompletedDeal.receiveCards || [])])
        : [];
    }

    if (cardPreview?.card) {
      visibleCards = [...visibleCards, ...collectTradeCardSetMetadataCards([cardPreview.card])];
    }

    const setCodes = [...new Set(visibleCards.map((card) => normalizeLookupValue(card.edition)).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId: requestedUserId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (isCancelled) {
          return;
        }

        setSetMetadata((currentState) => ({
          ...currentState,
          ...(data.sets || {})
        }));
      })
      .catch((fetchError) => {
        console.error("TradeFinder set metadata error:", fetchError);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    activeDraftSuggestion,
    activeAcceptedTradeId,
    activeCompletedTradeId,
    activePendingTradeId,
    activeSuggestionIndex,
    activeTab,
    activeTradesPayload.accepted,
    activeTradesPayload.pending,
    browseSuggestions,
    cardPreview,
    completedTradesPayload.deals,
    requestedUserId,
    setMetadata
  ]);

  useEffect(() => {
    let isDisposed = false;

    const loadTradeDeals = async () => {
      try {
        setLoadingTrades(true);
        const [activeResponse, completedResponse] = await Promise.all([
          apiFetch("/api/trades/active", { userId: requestedUserId }),
          apiFetch("/api/trades/completed", { userId: requestedUserId })
        ]);
        const activeData = await activeResponse.json().catch(() => ({}));
        const completedData = await completedResponse.json().catch(() => ({}));

        if (!activeResponse.ok) {
          throw new Error(activeData.error || "Could not load active trades");
        }

        if (!completedResponse.ok) {
          throw new Error(completedData.error || "Could not load completed trades");
        }

        if (isDisposed) {
          return;
        }

        setActiveTradesPayload({
          pending: Array.isArray(activeData.pending) ? activeData.pending : [],
          accepted: Array.isArray(activeData.accepted) ? activeData.accepted : [],
          summary: activeData.summary || { pendingCount: 0, acceptedCount: 0, awaitingResponseCount: 0, awaitingCounterpartyCount: 0 }
        });
        setCompletedTradesPayload({
          deals: Array.isArray(completedData.deals) ? completedData.deals : [],
          summary: completedData.summary || { completedCount: 0 }
        });
      } catch (err) {
        if (!isDisposed) {
          console.error("TradeFinder deal load error:", err);
          setError(err.message || "Could not load trades");
        }
      } finally {
        if (!isDisposed) {
          setLoadingTrades(false);
        }
      }
    };

    loadTradeDeals();

    return () => {
      isDisposed = true;
    };
  }, [requestedUserId]);

  const activeSuggestion = browseSuggestions[activeSuggestionIndex] || null;
  const activePendingTrade = activeTradesPayload.pending.find((deal) => Number(deal.id) === Number(activePendingTradeId)) || activeTradesPayload.pending[0] || null;
  const activeAcceptedTrade = activeTradesPayload.accepted.find((deal) => Number(deal.id) === Number(activeAcceptedTradeId)) || activeTradesPayload.accepted[0] || null;
  const activeCompletedTrade = completedTradesPayload.deals.find((deal) => Number(deal.id) === Number(activeCompletedTradeId)) || completedTradesPayload.deals[0] || null;
  const activeDraftListIndex = browseDrafts.findIndex((draft) => Number(draft?.id) === Number(activeDraft?.id));
  const activePendingTradeListIndex = activeTradesPayload.pending.findIndex((deal) => Number(deal.id) === Number(activePendingTrade?.id));
  const activeAcceptedTradeListIndex = activeTradesPayload.accepted.findIndex((deal) => Number(deal.id) === Number(activeAcceptedTrade?.id));
  const activeCompletedTradeListIndex = completedTradesPayload.deals.findIndex((deal) => Number(deal.id) === Number(activeCompletedTrade?.id));

  useEffect(() => {
    if (activeTradesPayload.pending.length === 0) {
      setActivePendingTradeId(null);
      return;
    }

    if (!activeTradesPayload.pending.some((deal) => Number(deal.id) === Number(activePendingTradeId))) {
      setActivePendingTradeId(activeTradesPayload.pending[0].id);
    }
  }, [activeTradesPayload.pending, activePendingTradeId]);

  useEffect(() => {
    if (activeTradesPayload.accepted.length === 0) {
      setActiveAcceptedTradeId(null);
      return;
    }

    if (!activeTradesPayload.accepted.some((deal) => Number(deal.id) === Number(activeAcceptedTradeId))) {
      setActiveAcceptedTradeId(activeTradesPayload.accepted[0].id);
    }
  }, [activeTradesPayload.accepted, activeAcceptedTradeId]);

  useEffect(() => {
    if (completedTradesPayload.deals.length === 0) {
      setActiveCompletedTradeId(null);
      return;
    }

    if (!completedTradesPayload.deals.some((deal) => Number(deal.id) === Number(activeCompletedTradeId))) {
      setActiveCompletedTradeId(completedTradesPayload.deals[0].id);
    }
  }, [completedTradesPayload.deals, activeCompletedTradeId]);

  const browseContextKey = activeSuggestion ? `browse:${activeSuggestion.counterparty.id}` : null;
  const draftContextKey = activeDraftSuggestion ? `draft:${activeDraftSuggestion.counterparty.id}` : null;
  const activePendingDealContextKey = activePendingTrade ? `deal:${activePendingTrade.id}` : null;
  const activeSuggestionTrade = activeSuggestion ? withTradeUsers(activeSuggestion, browsePayload.currentUser, activeSuggestion.counterparty) : null;
  const activeSuggestionDraft = activeSuggestionTrade ? buildTradeDraft(activeSuggestionTrade, excludedCardsByContext[browseContextKey] || []) : null;
  const matchingBrowseDraft = activeSuggestion
    ? browseDrafts.find((draft) => Number(draft?.counterparty?.id) === Number(activeSuggestion.counterparty?.id)) || null
    : null;
  const activeDraftTrade = activeDraftSuggestion ? withTradeUsers(activeDraftSuggestion, browsePayload.currentUser, activeDraftSuggestion.counterparty) : null;
  const activeDraftDraft = activeDraftTrade ? buildTradeDraft(activeDraftTrade, excludedCardsByContext[draftContextKey] || []) : null;
  const activePendingDealDraft = activePendingTrade
    ? buildTradeDraft(withTradeUsers(activePendingTrade, activePendingTrade.currentUser, activePendingTrade.counterparty), excludedCardsByContext[activePendingDealContextKey] || [])
    : null;
  const hasActiveDraftChanges = Boolean(draftContextKey) && (excludedCardsByContext[draftContextKey] || []).length > 0;
  const hasActivePendingDealDraftChanges = Boolean(activePendingDealContextKey) && (excludedCardsByContext[activePendingDealContextKey] || []).length > 0;
  const activeEditableContextKey = activeTab === TAB_DRAFTS ? draftContextKey : browseContextKey;
  const activeEditableTrade = activeTab === TAB_DRAFTS ? activeDraftTrade : activeSuggestionTrade;
  const activeEditableDraft = activeTab === TAB_DRAFTS ? activeDraftDraft : activeSuggestionDraft;
  const activeDraftAvailabilityConflict = activeDraftSuggestion
    ? draftAvailabilityConflictsByCounterpartyId[Number(activeDraftSuggestion.counterparty?.id)] || null
    : null;

  const refreshBrowseSuggestions = async ({ showLoading = false } = {}) => {
    if (showLoading) {
      setLoadingBrowse(true);
    }

    try {
      const response = await apiFetch(`/api/tradefinder/${requestedUserId}`, { userId: requestedUserId });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not refresh TradeFinder");
      }

      setBrowsePayload({
        currentUser: normalizeTradeFinderCurrentUser(data.currentUser, requestedUserId, selectedUser),
        suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
        summary: data.summary || { totalSuggestions: 0, totalSuggestionsBeforeRadius: 0, radiusFilteredSuggestions: 0, totalReceiveMatches: 0, totalSendMatches: 0 }
      });
      setActiveSuggestionIndex(0);
      setHasLoadedBrowseSuggestions(true);
      setError(null);
    } finally {
      if (showLoading) {
        setLoadingBrowse(false);
      }
    }
  };

  const refreshBrowseSuggestionsIfLoaded = async () => {
    if (!hasLoadedBrowseSuggestions) {
      return;
    }

    await refreshBrowseSuggestions();
  };

  const handleBrowseTrades = async () => {
    try {
      setError(null);
      await refreshBrowseSuggestions({ showLoading: true });
    } catch (err) {
      console.error("TradeFinder browse load error:", err);
      setError(err.message || "Could not load TradeFinder");
    }
  };

  const refreshBrowseDrafts = async () => {
    const response = await apiFetch("/api/trade-drafts", { userId: requestedUserId });
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || "Could not refresh trade drafts");
    }

    const nextDrafts = Array.isArray(data.drafts) ? data.drafts : [];
    setBrowseDrafts(nextDrafts);
    setDraftAvailabilityConflictsByCounterpartyId((currentState) => {
      const validCounterpartyIds = new Set(nextDrafts.map((draft) => Number(draft?.counterparty?.id)).filter((id) => Number.isInteger(id) && id > 0));
      const nextState = Object.fromEntries(
        Object.entries(currentState).filter(([counterpartyId]) => validCounterpartyIds.has(Number(counterpartyId)))
      );

      return Object.keys(nextState).length === Object.keys(currentState).length ? currentState : nextState;
    });
  };

  const refreshTradeLists = async () => {
    const [activeResponse, completedResponse] = await Promise.all([
      apiFetch("/api/trades/active", { userId: requestedUserId }),
      apiFetch("/api/trades/completed", { userId: requestedUserId })
    ]);
    const activeData = await activeResponse.json().catch(() => ({}));
    const completedData = await completedResponse.json().catch(() => ({}));

    if (!activeResponse.ok) {
      throw new Error(activeData.error || "Could not refresh active trades");
    }

    if (!completedResponse.ok) {
      throw new Error(completedData.error || "Could not refresh completed trades");
    }

    setActiveTradesPayload({
      pending: Array.isArray(activeData.pending) ? activeData.pending : [],
      accepted: Array.isArray(activeData.accepted) ? activeData.accepted : [],
      summary: activeData.summary || { pendingCount: 0, acceptedCount: 0, awaitingResponseCount: 0, awaitingCounterpartyCount: 0 }
    });
    setCompletedTradesPayload({
      deals: Array.isArray(completedData.deals) ? completedData.deals : [],
      summary: completedData.summary || { completedCount: 0 }
    });
  };

  const toggleExcludedCard = async (contextKey, side, card) => {
    const token = getExcludedCardToken(side, card);
    const currentTokens = excludedCardsByContext[contextKey] || [];
    const nextTokens = currentTokens.includes(token)
      ? currentTokens.filter((value) => value !== token)
      : [...currentTokens, token];

    setExcludedCardsByContext((currentState) => {
      return {
        ...currentState,
        [contextKey]: nextTokens
      };
    });

    const editableTrade = contextKey === browseContextKey
      ? activeSuggestionTrade
      : contextKey === draftContextKey
        ? activeDraftTrade
        : null;
    const editableCounterpartyId = contextKey === browseContextKey
      ? activeSuggestion?.counterparty?.id
      : contextKey === draftContextKey
        ? activeDraftSuggestion?.counterparty?.id
        : null;

    if (!editableTrade || !editableCounterpartyId) {
      return;
    }

    try {
      setError(null);

      const nextEditableDraft = buildTradeDraft(editableTrade, nextTokens);
      const response = await apiFetch(`/api/trade-drafts/${editableCounterpartyId}/demand-cards`, {
        userId: requestedUserId,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offerCards: nextEditableDraft.sendCards.map(serializeTradeCardForRequest),
          demandCards: nextEditableDraft.receiveCards.map(serializeTradeCardForRequest)
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not save trade draft");
      }

      await refreshBrowseDrafts();
      clearDraftAvailabilityConflict(editableCounterpartyId);
      setNotice("Trade draft saved. The draft expires after 4 hours if it is not proposed.");
    } catch (err) {
      console.error("Trade draft exclude save error:", err);
      setExcludedCardsByContext((currentState) => ({ ...currentState, [contextKey]: currentTokens }));
      setError(err.message || "Could not save trade draft");
    }
  };

  const handleBrowseAccept = async () => {
    if (!activeSuggestionDraft || !activeSuggestion) {
      return;
    }

    const nextOfferCards = matchingBrowseDraft
      ? mergeTradeCardCollections(matchingBrowseDraft.offerCards || [], activeSuggestionDraft.sendCards || [])
      : (activeSuggestionDraft.sendCards || []);
    const nextDemandCards = matchingBrowseDraft
      ? mergeTradeCardCollections(matchingBrowseDraft.demandCards || [], activeSuggestionDraft.receiveCards || [])
      : (activeSuggestionDraft.receiveCards || []);

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await apiFetch(`/api/trade-drafts/${activeSuggestion.counterparty.id}/demand-cards`, {
        userId: requestedUserId,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offerCards: nextOfferCards.map(serializeTradeCardForRequest),
          demandCards: nextDemandCards.map(serializeTradeCardForRequest)
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not save trade draft");
      }

      await refreshBrowseDrafts();
      clearDraftAvailabilityConflict(activeSuggestion.counterparty.id);
      setActiveDraftId(data.draft?.id || null);
      setActiveTab(TAB_DRAFTS);
      setNotice("Trade saved to Drafts. Propose it from the Drafts tab when you are ready.");
    } catch (err) {
      console.error("TradeFinder browse save error:", err);
      setError(err.message || "Could not save trade draft");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraftPropose = () => {
    if (!activeDraftDraft) {
      return;
    }

    setProposeTradeForm(createTradeMessageFormState());
    setIsProposeTradeModalOpen(true);
  };

  const handleRemoveDraft = async (draft) => {
    if (!draft?.counterparty?.id) {
      return;
    }

    const shouldRemoveDraft = await showConfirm({
      title: "Remove Draft",
      message: "Remove this saved draft? This only deletes the draft and does not affect any live trade.",
      confirmLabel: "Remove draft",
      tone: "danger"
    });

    if (!shouldRemoveDraft) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const counterpartyUserId = Number(draft.counterparty.id);
      const response = await apiFetch(`/api/trade-drafts/${counterpartyUserId}`, {
        userId: requestedUserId,
        method: "DELETE"
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not remove trade draft");
      }

      setExcludedCardsByContext((currentState) => {
        const nextState = { ...currentState };
        delete nextState[`draft:${counterpartyUserId}`];
        return nextState;
      });

      await refreshBrowseDrafts();
      clearDraftAvailabilityConflict(counterpartyUserId);
      await refreshBrowseSuggestionsIfLoaded();
      setNotice("Draft removed.");
    } catch (err) {
      console.error("Trade draft remove error:", err);
      setError(err.message || "Could not remove trade draft");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeProposeTradeModal = () => {
    if (isSubmitting) {
      return;
    }

    setIsProposeTradeModalOpen(false);
    setProposeTradeForm(createTradeMessageFormState());
  };

  const handleSubmitProposeTrade = async () => {
    if (!activeEditableDraft) {
      return;
    }

    const messageBody = String(proposeTradeForm.messageBody || "").trim();

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await apiFetch("/api/trades", {
        userId: requestedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          counterpartyUserId: activeEditableDraft.counterparty.id,
          offerCards: activeEditableDraft.sendCards.map(serializeTradeCardForRequest),
          demandCards: activeEditableDraft.receiveCards.map(serializeTradeCardForRequest),
          paymentAmount: activeEditableDraft.paymentAmount,
          paymentFromUserId: activeEditableDraft.paymentFromUserId
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data?.code === "TRADE_AVAILABILITY_CONFLICT") {
          const counterpartyId = Number(activeEditableDraft.counterparty.id);
          setDraftAvailabilityConflictsByCounterpartyId((currentState) => ({
            ...currentState,
            [counterpartyId]: Array.isArray(data.conflicts) ? data.conflicts : []
          }));
          setIsProposeTradeModalOpen(false);
          setError(formatTradeAvailabilityConflicts(data.conflicts));
          return;
        }

        throw new Error(data.error || "Could not propose trade");
      }

      if (activeEditableContextKey) {
        setExcludedCardsByContext((currentState) => ({ ...currentState, [activeEditableContextKey]: [] }));
      }
      clearDraftAvailabilityConflict(activeEditableDraft.counterparty.id);

      if (messageBody) {
        try {
          const messageResponse = await apiFetch(`/api/messages/users/${activeEditableDraft.counterparty.id}`, {
            userId: requestedUserId,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: messageBody })
          });
          const messageData = await messageResponse.json().catch(() => ({}));

          if (!messageResponse.ok) {
            throw new Error(messageData.error || "Could not send message");
          }
        } catch (messageError) {
          console.error("Trade propose message error:", messageError);
          setError("Trade proposal sent, but the message could not be sent.");
        }
      }

      setActivePendingTradeId(data.deal?.id || null);
      setActiveTab(TAB_PENDING);
      setIsProposeTradeModalOpen(false);
      setProposeTradeForm(createTradeMessageFormState());
      setNotice(messageBody ? "Trade proposal and message sent." : "Trade proposal sent.");

      try {
        await refreshTradeLists();
        await refreshBrowseSuggestionsIfLoaded();
        await refreshBrowseDrafts();
      } catch (refreshError) {
        console.error("Trade create refresh error:", refreshError);
        setNotice("Trade proposal sent. Some views may need a refresh.");
      }
    } catch (err) {
      console.error("Trade create error:", err);
      setError(err.message || "Could not propose trade");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePendingTradePrimaryAction = async () => {
    if (!activePendingDealDraft || !activePendingTrade) {
      return;
    }

    if (!hasActivePendingDealDraftChanges) {
      handleAcceptTrade();
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await apiFetch(`/api/trades/${activePendingTrade.id}/revisions`, {
        userId: requestedUserId,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offerCards: activePendingDealDraft.sendCards.map(serializeTradeCardForRequest),
          demandCards: activePendingDealDraft.receiveCards.map(serializeTradeCardForRequest),
          paymentAmount: activePendingDealDraft.paymentAmount,
          paymentFromUserId: activePendingDealDraft.paymentFromUserId
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not update trade");
      }

      if (activePendingDealContextKey) {
        setExcludedCardsByContext((currentState) => ({ ...currentState, [activePendingDealContextKey]: [] }));
      }

      await refreshTradeLists();
  await refreshBrowseSuggestionsIfLoaded();
      await refreshBrowseDrafts();
      setActivePendingTradeId(data.deal?.id || activePendingTrade.id);
      setNotice("Counter-offer sent.");
    } catch (err) {
      console.error("Trade pending action error:", err);
      setError(err.message || "Could not update trade");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptTrade = async () => {
    if (!activePendingTrade) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const response = await apiFetch(`/api/trades/${activePendingTrade.id}/accept`, {
        userId: requestedUserId,
        method: "POST"
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not update trade");
      }

      const acceptedTradeId = data.deal?.id || activePendingTrade.id;
      let acceptedNotice = "Trade accepted.";

      if (activePendingDealContextKey) {
        setExcludedCardsByContext((currentState) => ({ ...currentState, [activePendingDealContextKey]: [] }));
      }
      setActiveAcceptedTradeId(acceptedTradeId);
      setActiveTab(TAB_ACCEPTED);

      try {
        await refreshTradeLists();
        await refreshBrowseSuggestionsIfLoaded();
        await refreshBrowseDrafts();
      } catch (refreshError) {
        console.error("Trade accept refresh error:", refreshError);
        acceptedNotice = "Trade accepted. Some views may need a refresh.";
      }

      setNotice(acceptedNotice);
    } catch (err) {
      console.error("Trade accept error:", err);
      setError(err.message || "Could not update trade");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeCompleteTradeModal = () => {
    if (isSubmitting) {
      return;
    }

    setIsCompleteTradeModalOpen(false);
    setCompleteTradeForm(createTradeCompletionFormState());
  };

  const handleOpenCompleteTradeModal = () => {
    if (!activeAcceptedTrade || isSubmitting || activeAcceptedTrade.currentUserCompleted) {
      return;
    }

    setCompleteTradeForm(createTradeCompletionFormState());
    setIsCompleteTradeModalOpen(true);
  };

  const handleCompleteTrade = async () => {
    if (!activeAcceptedTrade) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await apiFetch(`/api/trades/${activeAcceptedTrade.id}/complete`, {
        userId: requestedUserId,
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          removeMatchedWants: completeTradeForm.removeMatchedWants,
          satisfactionRating: completeTradeForm.satisfactionRating,
          reviewRemarks: completeTradeForm.reviewRemarks
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not complete trade");
      }

      setIsCompleteTradeModalOpen(false);
      setCompleteTradeForm(createTradeCompletionFormState());
      await refreshTradeLists();
      await refreshBrowseSuggestionsIfLoaded();
      setNotice("Trade completion recorded.");
    } catch (err) {
      console.error("Trade complete error:", err);
      setError(err.message || "Could not complete trade");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelTrade = async (trade, nextTab) => {
    if (!trade) {
      return;
    }

    const shouldCancelTrade = await showConfirm({
      title: "Cancel Trade",
      message: "Cancel this trade? This will release all locked cards in the deal.",
      confirmLabel: "Cancel trade",
      tone: "danger"
    });

    if (!shouldCancelTrade) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const response = await apiFetch(`/api/trades/${trade.id}/cancel`, {
        userId: requestedUserId,
        method: "POST"
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not cancel trade");
      }

      setExcludedCardsByContext((currentState) => {
        const nextState = { ...currentState };
        delete nextState[`deal:${trade.id}`];
        delete nextState[`accepted:${trade.id}`];
        return nextState;
      });

      await refreshTradeLists();
  await refreshBrowseSuggestionsIfLoaded();
      setActiveTab(nextTab);
      setNotice("Trade cancelled.");
    } catch (err) {
      console.error("Trade cancel error:", err);
      setError(err.message || "Could not cancel trade");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCardPreview = ({ contextKey, side, card, editable = true }) => {
    const excludedTokens = excludedCardsByContext[contextKey] || [];
    const editableDraft = contextKey === browseContextKey
      ? activeSuggestionDraft
      : contextKey === draftContextKey
        ? activeDraftDraft
        : null;
    const selectedGroupCards = editable && side === "receive" && editableDraft && card?.variant_group_key
      ? (editableDraft.receiveCards || []).filter((draftCard) => draftCard.variant_group_key === card.variant_group_key)
      : [];
    const {
      selectedKeys: selectedVariantKeys,
      lockedKeys: lockedVariantKeys,
      preferredGroupKey
    } = buildPreviewVariantSelectionState(card, selectedGroupCards, card);

    setCardPreview({
      contextKey,
      side,
      card,
      canExclude: editable,
      canViewVariants: side === "receive" && hasTradeCardVariantChoices(card),
      canManageVariants: editable && side === "receive" && contextKey === draftContextKey && hasTradeCardVariantChoices(card),
      selectedVariantKeys,
      preferredGroupKey,
      lockedVariantKeys,
      isExcluded: excludedTokens.includes(getExcludedCardToken(side, card))
    });
  };

  const togglePreviewCardExclusion = () => {
    if (!cardPreview?.contextKey || !cardPreview?.card) {
      return;
    }

    toggleExcludedCard(cardPreview.contextKey, cardPreview.side, cardPreview.card);
    setCardPreview((currentPreview) => currentPreview
      ? { ...currentPreview, isExcluded: !currentPreview.isExcluded }
      : currentPreview);
  };

  const handleTogglePreviewVariantSelection = (variantCard, nextQty) => {
    if (!cardPreview?.card?.variant_group_key || !variantCard) {
      return;
    }

    setCardPreview((currentPreview) => {
      if (!currentPreview) {
        return currentPreview;
      }

      const variantChoices = buildGroupedVariantChoices(currentPreview.card);
      const matchingChoice = variantChoices.find((choice) => choice.variant_group_choice_key === getTradeCardVariantGroupKey(variantCard));

      if (!matchingChoice) {
        return currentPreview;
      }

      const lockedQtyByGroup = buildVariantSelectionQtyByGroup(currentPreview.card, currentPreview.lockedVariantKeys || []);
      const maxSelectableQty = Math.max(0, matchingChoice.availableQty - (lockedQtyByGroup.get(matchingChoice.variant_group_choice_key) || 0));
      const boundedQty = Math.max(0, Math.min(maxSelectableQty, Number(nextQty) || 0));
      const nextKeys = replaceVariantSelectionKeysForChoice(currentPreview.card, currentPreview.selectedVariantKeys || [], matchingChoice, boundedQty);

      return {
        ...currentPreview,
        selectedVariantKeys: nextKeys
      };
    });
  };

  const handleSavePreviewVariantSelection = async () => {
    const editableDraft = cardPreview?.contextKey === browseContextKey
      ? activeSuggestionDraft
      : cardPreview?.contextKey === draftContextKey
        ? activeDraftDraft
        : null;
    const editableCounterpartyId = cardPreview?.contextKey === browseContextKey
      ? activeSuggestion?.counterparty?.id
      : cardPreview?.contextKey === draftContextKey
        ? activeDraftSuggestion?.counterparty?.id
        : null;

    if (!cardPreview?.card?.variant_group_key || !editableDraft || !editableCounterpartyId) {
      return;
    }

    const nextGroupCards = buildVariantSelectionCards(cardPreview.card, [
      ...(cardPreview.lockedVariantKeys || []),
      ...(cardPreview.selectedVariantKeys || [])
    ]);
    const nextDemandCards = [
      ...(editableDraft.receiveCards || []).filter((draftCard) => draftCard.variant_group_key !== cardPreview.card.variant_group_key),
      ...nextGroupCards
    ].sort((leftCard, rightCard) => roundCurrency(rightCard.line_total) - roundCurrency(leftCard.line_total)
      || String(leftCard.card || "").localeCompare(String(rightCard.card || "")));

    try {
      setIsSavingVariantSelection(true);
      setError(null);

      const response = await apiFetch(`/api/trade-drafts/${editableCounterpartyId}/demand-cards`, {
        userId: requestedUserId,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offerCards: (editableDraft.sendCards || []).map(serializeTradeCardForRequest),
          demandCards: nextDemandCards.map(serializeTradeCardForRequest)
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not save trade draft");
      }

      await refreshBrowseDrafts();
      clearDraftAvailabilityConflict(editableCounterpartyId);
      setCardPreview(null);
      setNotice("Trade draft saved. The draft expires after 4 hours if it is not proposed.");
    } catch (err) {
      console.error("Trade draft variant save error:", err);
      setError(err.message || "Could not save trade draft");
    } finally {
      setIsSavingVariantSelection(false);
    }
  };

  const openCounterpartyTraders = (counterparty) => {
    const counterpartyUserId = Number(counterparty?.id);

    if (!Number.isInteger(counterpartyUserId) || counterpartyUserId < 1) {
      return;
    }

    navigate(buildBrowseUserPath(counterpartyUserId, counterparty?.username), {
      state: {
        userId: counterpartyUserId,
        username: counterparty?.username || `User ${counterpartyUserId}`
      }
    });
  };

  const pendingTradePrimaryActionLabel = !activePendingTrade
    ? null
    : hasActivePendingDealDraftChanges
        ? "Propose changes"
        : activePendingTrade.awaitingResponse
          ? "Accept trade"
          : activePendingTrade.awaitingCounterparty
            ? "Awaiting response"
            : "Accept trade";
  const pendingTradePrimaryActionTone = activePendingTrade?.awaitingCounterparty
      ? "grey"
      : "green";
  const pendingTradePrimaryActionDisabled = isSubmitting || !activePendingTrade || activePendingTrade.awaitingCounterparty;
  const isPendingTradeEditable = Boolean(activePendingTrade) && !activePendingTrade.awaitingCounterparty;
  const currentTradeFinderSettings = browsePayload.currentUser?.tradeFinderSettings || DEFAULT_TRADE_FINDER_SETTINGS;
  const visibleBrowseMatches = browseSuggestions.length;
  const shownBrowseMatches = browseSuggestions.length;
  const browseSuggestionLimit = Number(browsePayload.summary.suggestionLimit) || 0;
  const hiddenByMinimumSaleMatches = Number(browsePayload.summary.minSaleFilteredSuggestions) || 0;
  const hiddenByMinimumBuyMatches = Number(browsePayload.summary.minBuyFilteredSuggestions) || 0;
  const hiddenByRadiusMatches = Number(browsePayload.summary.radiusFilteredSuggestions) || 0;
  const hiddenBySocialMatches = Number(browsePayload.summary.socialFilteredSuggestions) || 0;
  const hiddenBrowseMatches = hiddenByMinimumSaleMatches + hiddenByMinimumBuyMatches + hiddenByRadiusMatches + hiddenBySocialMatches;
  const totalPotentialBrowseMatches = Math.max(Number(browsePayload.summary.totalSuggestionsBeforeThresholds) || 0, visibleBrowseMatches);
  const draftSecondaryMessage = [
    hasActiveDraftChanges ? "Draft changed" : null,
    activeDraftAvailabilityConflict ? "Needs review" : null
  ].filter(Boolean).join(" · ") || null;

  const handleOpenHiddenMatchesSummary = async () => {
    if (hiddenBrowseMatches < 1) {
      return;
    }

    const summaryLines = [
      `${hiddenBrowseMatches} ${hiddenBrowseMatches === 1 ? "trade is" : "trades are"} currently hidden in Suggested trades.`
    ];

    if (hiddenByMinimumSaleMatches > 0) {
      summaryLines.push(`${hiddenByMinimumSaleMatches} ${hiddenByMinimumSaleMatches === 1 ? "trade is" : "trades are"} hidden by your Minimum sale filter.`);
    }

    if (hiddenByMinimumBuyMatches > 0) {
      summaryLines.push(`${hiddenByMinimumBuyMatches} ${hiddenByMinimumBuyMatches === 1 ? "trade is" : "trades are"} hidden by your Minimum buy filter.`);
    }

    if (hiddenByRadiusMatches > 0) {
      summaryLines.push(`${hiddenByRadiusMatches} ${hiddenByRadiusMatches === 1 ? "trade is" : "trades are"} hidden by the mutual radius filter.`);
    }

    if (hiddenBySocialMatches > 0) {
      summaryLines.push(`${hiddenBySocialMatches} ${hiddenBySocialMatches === 1 ? "trade is" : "trades are"} hidden by your Social filter.`);
    }

    await showAlert({
      title: "Hidden suggested trades",
      message: summaryLines.join("\n\n")
    });
  };

  return (
    <div className="app-responsive-shell tradefinder-page">
      <div className="tradefinder-page-header">
        <div>
          <h1 style={{ margin: "0 0 16px", fontFamily: "inherit", fontSize: "2em", fontWeight: 700 }}>TradeFinder</h1>
          <p className="account-copy">
            Browse, edit and propose suggested trade matches.
          </p>
        </div>
      </div>

      <div className="tradefinder-tabs">
        <TabButton label="Suggestions" isActive={activeTab === TAB_BROWSE} onClick={() => setActiveTab(TAB_BROWSE)} />
        <TabButton label="Drafts" isActive={activeTab === TAB_DRAFTS} badge={browseDrafts.length || undefined} onClick={() => setActiveTab(TAB_DRAFTS)} />
        <TabButton label="Pending" isActive={activeTab === TAB_PENDING} badge={activeTradesPayload.summary.pendingCount || undefined} onClick={() => setActiveTab(TAB_PENDING)} />
        <TabButton label="Accepted" isActive={activeTab === TAB_ACCEPTED} badge={activeTradesPayload.summary.acceptedCount || undefined} onClick={() => setActiveTab(TAB_ACCEPTED)} />
        <TabButton label="Completed" isActive={activeTab === TAB_COMPLETED} onClick={() => setActiveTab(TAB_COMPLETED)} />
      </div>

      <div className="tradefinder-settings-summary">
        <div className="tradefinder-settings-summary-items">
          <Link to="/account/tradefinder-settings" style={{ fontSize: "12px", color: "#0f766e", fontWeight: 700, textDecoration: "none" }}>
            TradeFinder Settings:
          </Link>
          <div style={{ fontSize: "13px", color: "#475569" }}>Minimum sale: <strong>{formatTradeFinderSetting(currentTradeFinderSettings.minSaleValue)}</strong></div>
          <div style={{ fontSize: "13px", color: "#475569" }}>Minimum buy: <strong>{formatTradeFinderSetting(currentTradeFinderSettings.minBuyValue)}</strong></div>
          <div style={{ fontSize: "13px", color: "#475569" }}>Radius: <strong>{formatTradeFinderRadiusLabel(currentTradeFinderSettings)}</strong></div>
          <div style={{ fontSize: "13px", color: "#475569" }}>Social: <strong>{formatTradeFinderSocialLabel(currentTradeFinderSettings)}</strong></div>
          {activeTab === TAB_BROWSE && hasLoadedBrowseSuggestions ? (
            <div style={{ fontSize: "13px", color: "#475569" }}>
                Matches: <strong>{shownBrowseMatches} shown</strong>
                {visibleBrowseMatches > shownBrowseMatches ? ` / ${visibleBrowseMatches} visible` : ""}
                {browseSuggestionLimit > 0 && visibleBrowseMatches > browseSuggestionLimit ? ` / limit ${browseSuggestionLimit}` : ""}
              {hiddenBrowseMatches > 0 ? (
                <>
                  {" / "}
                  <button
                    type="button"
                    onClick={handleOpenHiddenMatchesSummary}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      color: "#0f766e",
                      fontSize: "13px",
                      fontWeight: 700,
                      textDecoration: "underline",
                      textUnderlineOffset: "3px",
                      cursor: "pointer"
                    }}
                  >
                    {hiddenBrowseMatches} hidden
                  </button>
                </>
              ) : null}
              {totalPotentialBrowseMatches > visibleBrowseMatches ? ` / ${totalPotentialBrowseMatches} total` : ""}
            </div>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="account-error" style={{ marginTop: 0, marginBottom: "16px" }}>
          {error}
        </div>
      ) : null}

      {activeTab === TAB_BROWSE ? (
        !hasLoadedBrowseSuggestions ? (
          <div className="tradefinder-empty-state tradefinder-empty-state--browse-cta">
            <div className="tradefinder-empty-state__title">Load suggested trades</div>
            <div className="tradefinder-empty-state__body">
              Start here to find trades that match your current TradeFinder filters and then save those you want to work on into Drafts.
            </div>
            <button
              type="button"
              onClick={handleBrowseTrades}
              className="app-button-base tradefinder-browse-cta-button"
              disabled={loadingBrowse}
            >
              {loadingBrowse ? "Loading suggested trades..." : "Run TradeFinder Tool"}
            </button>
          </div>
        ) : loadingBrowse ? (
          <div className="tradefinder-empty-state">Loading trade suggestions...</div>
        ) : (
          <div className="app-responsive-split tradefinder-master-layout">
            <aside className="tradefinder-sidebar">
              <ResponsiveTradeSidebar
                items={browseSuggestions}
                activeIndex={activeSuggestionIndex}
                onSelectIndex={setActiveSuggestionIndex}
                getItemKey={(suggestion) => suggestion.counterparty.id}
                emptyState={(
                  <div className="tradefinder-empty-state tradefinder-empty-state--compact">
                    No trade suggestions yet. If you have cards in 'My Traders' and in 'My Wants', try lowering the TradeFinder minimum values in My Account.
                  </div>
                )}
                modalTitle="All suggested trades"
                renderItem={(suggestion, index, isActive, onClick) => (
                  <SuggestionListItem
                    suggestion={suggestion}
                    isActive={isActive}
                    onClick={onClick}
                    onOpenCounterparty={openCounterpartyTraders}
                  />
                )}
              />
            </aside>
            <section className="tradefinder-content">
              {activeSuggestion && activeSuggestionDraft ? (
                <TradeWorkspace
                  trade={activeSuggestion}
                  currentUser={browsePayload.currentUser}
                  counterparty={activeSuggestion.counterparty}
                  contextKey={browseContextKey}
                  setMetadata={setMetadata}
                  pricingCurrencySettings={pricingCurrencySettings}
                  pricingExchangeRates={pricingExchangeRates}
                  excludedCards={excludedCardsByContext[browseContextKey] || []}
                  onCardPreview={(preview) => openCardPreview({ ...preview, editable: true })}
                  onToggleExcludedCard={toggleExcludedCard}
                  primaryActionLabel="Save to drafts"
                  onPrimaryAction={handleBrowseAccept}
                  primaryActionDisabled={isSubmitting || (activeSuggestionDraft.sendCards.length === 0 && activeSuggestionDraft.receiveCards.length === 0)}
                  primaryActionTone="green"
                  onOpenCounterparty={openCounterpartyTraders}
                  title={`${browsePayload.currentUser.username} ↔ ${activeSuggestion.counterparty.username}`}
                  subtitle="Suggested trade"
                />
              ) : null}
            </section>
          </div>
        )
      ) : null}

      {activeTab === TAB_DRAFTS ? (
        <div className="app-responsive-split tradefinder-master-layout">
          <aside className="tradefinder-sidebar">
            <ResponsiveTradeSidebar
              items={browseDrafts}
              activeIndex={activeDraftListIndex}
              onSelectIndex={(index) => setActiveDraftId(browseDrafts[index]?.id ?? null)}
              getItemKey={(draft) => `draft:${draft.id}`}
              emptyState={(
                <div className="tradefinder-empty-state tradefinder-empty-state--compact">
                  No saved drafts.
                </div>
              )}
              modalTitle="All saved drafts"
              renderItem={(draft, index, isActive, onClick) => {
                const draftSuggestion = createBrowseSuggestionFromDraft(draft, browsePayload.currentUser);

                return (
                  <SuggestionListItem
                    suggestion={draftSuggestion}
                    isActive={isActive}
                    onClick={onClick}
                    onOpenCounterparty={openCounterpartyTraders}
                  />
                );
              }}
            />
          </aside>
          <section className="tradefinder-content">
            {activeDraftSuggestion && activeDraftDraft ? (
              <TradeWorkspace
                trade={activeDraftSuggestion}
                currentUser={browsePayload.currentUser}
                counterparty={activeDraftSuggestion.counterparty}
                contextKey={draftContextKey}
                setMetadata={setMetadata}
                pricingCurrencySettings={pricingCurrencySettings}
                pricingExchangeRates={pricingExchangeRates}
                excludedCards={excludedCardsByContext[draftContextKey] || []}
                onCardPreview={(preview) => openCardPreview({ ...preview, editable: true })}
                onToggleExcludedCard={toggleExcludedCard}
                primaryActionLabel="Propose trade"
                onPrimaryAction={handleDraftPropose}
                primaryActionDisabled={isSubmitting || Boolean(activeDraftAvailabilityConflict) || (activeDraftDraft.sendCards.length === 0 && activeDraftDraft.receiveCards.length === 0)}
                primaryActionTone="green"
                secondaryActionLabel="Remove draft"
                onSecondaryAction={() => handleRemoveDraft(activeDraft)}
                secondaryActionDisabled={isSubmitting}
                secondaryActionTone="danger"
                secondaryMessage={draftSecondaryMessage}
                onOpenCounterparty={openCounterpartyTraders}
                title={`${browsePayload.currentUser.username} ↔ ${activeDraftSuggestion.counterparty.username}`}
                subtitle="Saved draft"
              />
            ) : (
              <div className="tradefinder-empty-state">
                Select a draft to continue editing it.
              </div>
            )}
          </section>
        </div>
      ) : null}

      {activeTab === TAB_PENDING ? (
        loadingTrades ? (
          <div className="tradefinder-empty-state">Loading pending trades...</div>
        ) : (
          <div className="app-responsive-split tradefinder-master-layout">
            <aside className="tradefinder-sidebar">
              <ResponsiveTradeSidebar
                items={activeTradesPayload.pending}
                activeIndex={activePendingTradeListIndex}
                onSelectIndex={(index) => setActivePendingTradeId(activeTradesPayload.pending[index]?.id ?? null)}
                getItemKey={(deal) => `pending-${deal.id}`}
                emptyState={(
                  <div className="tradefinder-empty-state tradefinder-empty-state--compact">
                    No pending trades.
                  </div>
                )}
                modalTitle="All pending trades"
                renderItem={(deal, index, isActive, onClick) => (
                  <TradeDealListItem
                    deal={deal}
                    isActive={isActive}
                    onClick={onClick}
                    onOpenCounterparty={openCounterpartyTraders}
                  />
                )}
              />
            </aside>
            <section className="tradefinder-content">
              {activePendingTrade && activePendingDealDraft ? (
                <TradeWorkspace
                  trade={activePendingTrade}
                  currentUser={activePendingTrade.currentUser}
                  counterparty={activePendingTrade.counterparty}
                  contextKey={activePendingDealContextKey}
                  variantEdits={null}
                  setMetadata={setMetadata}
                  pricingCurrencySettings={pricingCurrencySettings}
                  pricingExchangeRates={pricingExchangeRates}
                  excludedCards={excludedCardsByContext[activePendingDealContextKey] || []}
                  onCardPreview={(preview) => openCardPreview({ ...preview, editable: isPendingTradeEditable })}
                  onToggleExcludedCard={isPendingTradeEditable ? toggleExcludedCard : null}
                  primaryActionLabel={pendingTradePrimaryActionLabel}
                  onPrimaryAction={handlePendingTradePrimaryAction}
                  primaryActionDisabled={pendingTradePrimaryActionDisabled}
                  primaryActionTone={pendingTradePrimaryActionTone}
                  secondaryActionLabel="Cancel trade"
                  onSecondaryAction={() => handleCancelTrade(activePendingTrade, TAB_PENDING)}
                  secondaryActionDisabled={isSubmitting}
                  secondaryActionTone="danger"
                  secondaryMessage={activePendingTrade.awaitingCounterparty ? null : (hasActivePendingDealDraftChanges ? "Draft changed" : null)}
                  onOpenCounterparty={openCounterpartyTraders}
                  sidePanel={(
                    <UserConversationPanel
                      userId={requestedUserId}
                      currentUserLabel={activePendingTrade.currentUser?.username || `User ${requestedUserId}`}
                      counterparty={activePendingTrade.counterparty}
                      onOpenCounterparty={openCounterpartyTraders}
                      refreshKey={`pending:${activePendingTrade.id}:${activePendingTrade.status}:${activePendingTrade.updatedAt || ""}`}
                      emptySelectionMessage="Select a pending trade to open the conversation."
                    />
                  )}
                  title={`${activePendingTrade.currentUser.username} ↔ ${activePendingTrade.counterparty.username}`}
                  subtitle="Pending trade"
                />
              ) : (
                <div className="tradefinder-empty-state">
                  Select a trade to review it.
                </div>
              )}
            </section>
          </div>
        )
      ) : null}

      {activeTab === TAB_ACCEPTED ? (
        loadingTrades ? (
          <div className="tradefinder-empty-state">Loading accepted trades...</div>
        ) : (
          <div className="app-responsive-split tradefinder-master-layout">
            <aside className="tradefinder-sidebar">
              <ResponsiveTradeSidebar
                items={activeTradesPayload.accepted}
                activeIndex={activeAcceptedTradeListIndex}
                onSelectIndex={(index) => setActiveAcceptedTradeId(activeTradesPayload.accepted[index]?.id ?? null)}
                getItemKey={(deal) => `accepted-${deal.id}`}
                emptyState={(
                  <div className="tradefinder-empty-state tradefinder-empty-state--compact">
                    No accepted trades yet.
                  </div>
                )}
                modalTitle="All accepted trades"
                renderItem={(deal, index, isActive, onClick) => (
                  <TradeDealListItem
                    deal={deal}
                    isActive={isActive}
                    onClick={onClick}
                    onOpenCounterparty={openCounterpartyTraders}
                  />
                )}
              />
            </aside>
            <section className="tradefinder-content">
              {activeAcceptedTrade ? (
                <TradeWorkspace
                  trade={activeAcceptedTrade}
                  currentUser={activeAcceptedTrade.currentUser}
                  counterparty={activeAcceptedTrade.counterparty}
                  contextKey={`accepted:${activeAcceptedTrade.id}`}
                  variantEdits={null}
                  setMetadata={setMetadata}
                  pricingCurrencySettings={pricingCurrencySettings}
                  pricingExchangeRates={pricingExchangeRates}
                  excludedCards={[]}
                  onCardPreview={(preview) => openCardPreview({ ...preview, editable: false })}
                  primaryActionLabel={activeAcceptedTrade.currentUserCompleted ? null : "Complete trade"}
                  onPrimaryAction={handleOpenCompleteTradeModal}
                  primaryActionDisabled={isSubmitting || activeAcceptedTrade.currentUserCompleted}
                  primaryActionTone={activeAcceptedTrade.currentUserCompleted ? "grey" : "green"}
                  secondaryActionLabel="Cancel trade"
                  onSecondaryAction={() => handleCancelTrade(activeAcceptedTrade, TAB_ACCEPTED)}
                  secondaryActionDisabled={isSubmitting}
                  secondaryActionTone="danger"
                  secondaryMessage="Accepted"
                  onOpenCounterparty={openCounterpartyTraders}
                  sidePanel={(
                    <UserConversationPanel
                      userId={requestedUserId}
                      currentUserLabel={activeAcceptedTrade.currentUser?.username || `User ${requestedUserId}`}
                      counterparty={activeAcceptedTrade.counterparty}
                      onOpenCounterparty={openCounterpartyTraders}
                      refreshKey={`accepted:${activeAcceptedTrade.id}:${activeAcceptedTrade.status}:${activeAcceptedTrade.updatedAt || ""}`}
                      emptySelectionMessage="Select an accepted trade to open the conversation."
                    />
                  )}
                  title={`${activeAcceptedTrade.currentUser.username} ↔ ${activeAcceptedTrade.counterparty.username}`}
                  subtitle="Accepted trade"
                />
              ) : (
                <div className="tradefinder-empty-state">
                  Select a trade to review it.
                </div>
              )}
            </section>
          </div>
        )
      ) : null}

      {activeTab === TAB_COMPLETED ? (
        loadingTrades ? (
          <div className="tradefinder-empty-state">Loading completed trades...</div>
        ) : (
          <div className="app-responsive-split tradefinder-master-layout">
            <aside className="tradefinder-sidebar">
              <ResponsiveTradeSidebar
                items={completedTradesPayload.deals}
                activeIndex={activeCompletedTradeListIndex}
                onSelectIndex={(index) => setActiveCompletedTradeId(completedTradesPayload.deals[index]?.id ?? null)}
                getItemKey={(deal) => `completed-${deal.id}`}
                emptyState={(
                  <div className="tradefinder-empty-state tradefinder-empty-state--compact">
                    No completed trades yet.
                  </div>
                )}
                modalTitle="All completed trades"
                renderItem={(deal, index, isActive, onClick) => (
                  <TradeDealListItem
                    deal={deal}
                    isActive={isActive}
                    onClick={onClick}
                    onOpenCounterparty={openCounterpartyTraders}
                  />
                )}
              />
            </aside>
            <section className="tradefinder-content">
              {activeCompletedTrade ? (
                <TradeWorkspace
                  trade={activeCompletedTrade}
                  currentUser={activeCompletedTrade.currentUser}
                  counterparty={activeCompletedTrade.counterparty}
                  contextKey={`completed:${activeCompletedTrade.id}`}
                  variantEdits={null}
                  setMetadata={setMetadata}
                  pricingCurrencySettings={pricingCurrencySettings}
                  pricingExchangeRates={pricingExchangeRates}
                  excludedCards={[]}
                  onCardPreview={(preview) => openCardPreview({ ...preview, editable: false })}
                  primaryActionLabel={null}
                  onPrimaryAction={null}
                  primaryActionDisabled
                  secondaryMessage="Completed"
                  onOpenCounterparty={openCounterpartyTraders}
                  sidePanel={(
                    <UserConversationPanel
                      userId={requestedUserId}
                      currentUserLabel={activeCompletedTrade.currentUser?.username || `User ${requestedUserId}`}
                      counterparty={activeCompletedTrade.counterparty}
                      onOpenCounterparty={openCounterpartyTraders}
                      refreshKey={`completed:${activeCompletedTrade.id}:${activeCompletedTrade.status}:${activeCompletedTrade.updatedAt || ""}`}
                      emptySelectionMessage="Select a completed trade to open the conversation."
                    />
                  )}
                  title={`${activeCompletedTrade.currentUser.username} ↔ ${activeCompletedTrade.counterparty.username}`}
                  subtitle="Completed trade"
                />
              ) : (
                <div className="tradefinder-empty-state">
                  Select a completed trade to inspect it.
                </div>
              )}
            </section>
          </div>
        )
      ) : null}

      <TradeCardPreviewModal
        preview={cardPreview}
        isSavingSelection={isSavingVariantSelection}
        onClose={() => setCardPreview(null)}
        onToggleExclude={togglePreviewCardExclusion}
        onToggleVariantSelection={handleTogglePreviewVariantSelection}
        onSaveVariantSelection={handleSavePreviewVariantSelection}
        setMetadata={setMetadata}
      />

      <ProposeTradeModal
        isOpen={isProposeTradeModalOpen}
        trade={activeEditableTrade}
        value={proposeTradeForm}
        isSubmitting={isSubmitting}
        onClose={closeProposeTradeModal}
        onChange={(field, nextValue) => {
          setProposeTradeForm((currentValue) => ({
            ...currentValue,
            [field]: nextValue
          }));
        }}
        onSubmit={handleSubmitProposeTrade}
      />

      <CompleteTradeModal
        isOpen={isCompleteTradeModalOpen}
        trade={activeAcceptedTrade}
        value={completeTradeForm}
        isSubmitting={isSubmitting}
        onClose={closeCompleteTradeModal}
        onChange={(field, nextValue) => {
          setCompleteTradeForm((currentValue) => ({
            ...currentValue,
            [field]: nextValue
          }));
        }}
        onSubmit={handleCompleteTrade}
      />

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </div>
  );
}